(function(){
  'use strict';

  /* =========================================
   Module: Configuration & State
   ========================================= */
  const state = {
    currentStep: 0,
    selectedCaseId: null,
    selectedCaseName: '',
    selectedCaseSku: '',
    casePriceCents: null,
    casePricePromise: Promise.resolve(null),
    totalSlots: 0,
    steps: [],
    previewDataUrl: '',
    prefetchedSteps: new Set(),
    previewHitAreas: []
  };

  const priceCache = new Map();
  const moneyFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  });

  const localeTag = (document.documentElement.lang || navigator.language || '').toLowerCase();
  const isGermanLocale = localeTag.startsWith('de');

  const normalizeLabel = (value) => (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const normalizeTypeKey = (value) => {
    const normalized = normalizeLabel(value);
    if (normalized.includes('breakfast') || normalized.includes('buckels')) return 'buckelsmesser';
    if (normalized.includes('steak')) return 'steakmesser';
    return normalized;
  };

  const normalizeBladeKey = (value) => {
    const normalized = normalizeLabel(value);
    if (['wellenschliff', 'serrated', 'serrated edge', 'with serration'].includes(normalized)) return 'wellenschliff';
    if (['glattschliff', 'straight', 'straight edge', 'without serration'].includes(normalized)) return 'glattschliff';
    return normalized;
  };

  const normalizeHandleKey = (value) => {
    const normalized = normalizeLabel(value);
    if (normalized === 'walnut') return 'walnuss';
    if (normalized === 'cherry') return 'kirsche';
    if (normalized === 'beech') return 'buche';
    if (normalized === 'olive') return 'olive';
    if (normalized === 'padouk') return 'padouk';
    if (normalized === 'bloak') return 'bloak';
    return normalized;
  };

  const CUSTOM_SKU_SUFFIX = '.CUSTOM';
  const PREVIEW_CROSS_ORIGIN = 'anonymous';
  const MAX_PREVIEW_DATA_URL_LENGTH = 20000;
  const ENGRAVING_MAX_LENGTH = 25;
  let cartPriceRequestId = 0;

  const PRICE_CARRIER_VARIANT_ID = window.setConfiguratorPriceCarrierId || null;
  const ENGRAVING_VARIANT_ID = window.setConfiguratorEngravingVariantId || '55259765309705';
  const SET_PRICE_VARIANTS = window.setConfiguratorSetPriceVariants || {
    2: {
      noEngraving: '56686566572297',
      withEngraving: '56649622290697'
    },
    4: {
      noEngraving: '56686566605065',
      withEngraving: '56649622323465'
    },
    6: {
      noEngraving: '56686566637833',
      withEngraving: '56649622356233'
    }
  };

  const CASE_VARIANT_IDS = window.setCaseVariantIds || {};
  const CASE_SELECTION_ENABLED = window.setCaseSelectionEnabled !== false;
  const SET_STATE_PROPERTY_KEY = '_set_state';
  const SET_STATE_QUERY_PARAM = 'set_state';
  const SET_ID_PROPERTY_KEY = '_SetID';
  const SET_COMPONENT_PROPERTY_KEY = '_Set-Komponente';
  const SET_PRICE_VARIANT_PROPERTY_KEY = '_Setpreis-Variante';
  const SET_CONTENT_PROPERTY_KEY = 'Set Inhalt';
  const SET_PREVIEW_STORAGE_PREFIX = 'set_preview:';
  const SET_PREVIEW_STORAGE_LIMIT = 12;
  const CONFIGURATOR_SUMMARY_ANCHOR = '#summary-step';
  const MOBILE_FULLSCREEN_QUERY = '(max-width: 959px)';

  const defaultStrings = {
    progress: {
      case: 'Holzkästchen',
      slot: 'Slot %i',
      summary: 'Übersicht'
    },
    labels: {
      type: '1. Messertyp',
      blade: '2. Klinge',
      handle: '3. Griff',
      engrave: '4. Personalisierte Lasergravur'
    },
    infoText: {
      type: `
        Wähle hier den Messertyp:<br><br>
        <strong>Buckelsmesser:</strong> Das Buckelsmesser ist ein echter Alleskönner fürs Frühstück – perfekt zum Streichen, Schneiden und Portionieren von Brötchen, Käse oder Aufschnitt. Die breite Klinge sorgt für müheloses Arbeiten und sanfte Schnitte.<br><br>
        <strong>Steakmesser:</strong> Das Steakmesser ist für wahre Fleischliebhaber gemacht. Mit seiner scharfen, spitz zulaufenden Klinge schneidest du Steaks, Fleisch oder Pizza mühelos, ohne die Fasern zu zerreißen.
      `,
      blade: `
        Wähle hier die Schleifart der Klinge:<br><br>
        <strong>Wellenschliff:</strong> Ideal für knusprige Krusten und harte Oberflächen – der Wellenschliff gleitet mühelos durch Brot, Tomaten oder Fleisch mit Kruste.<br><br>
        <strong>Glattschliff:</strong> Der klassische Allrounder – für präzise, saubere Schnitte bei Obst, Gemüse oder Fleisch ohne Widerstand.
      `,
      handle: `
        Wähle hier deinen Holzgriff aus unserer hauseigenen Schreinerei:<br><br>
<strong>Walnuss aus den USA:</strong> Dunkel, robust, edel gemasert – bringt warme Eleganz in die Küche.<br>
<strong>Padouk aus Gabun:</strong> Exotisch, rötlich, extrem hart – dunkelt charaktervoll nach.<br>
<strong>Olive aus Italien:</strong> Markante Maserung, mediterran, langlebig – ein Hauch Süden.<br>
<strong>Kirsche aus Deutschland:</strong> Feinporig, warm, regional – natürlich schön und angenehm.<br>
<strong>Bloak – Räuchereiche:</strong> Dunkel, massiv, elegant – markant durch Räucherung.<br>
<strong>Buche aus Deutschland:</strong> Hell, klassisch, strapazierfähig – liegt gut in der Hand.

      `,
      engrave: `
        <strong>Personalisierte Lasergravur:</strong><br>
        Verleihe Deinem Messer eine persönliche Note mit einer präzisen Lasergravur. Dein individueller Wunschtext (max. 25 Zeichen, ausschließlich Buchstaben und Zahlen) wird dauerhaft und hochwertig eingraviert – für ein edles, einzigartiges Finish.<br><br>
        (Aufpreis: 2er Set: +10 €, 4er Set: +15 €, 6er Set: +20 €)
      `
    },
    placeholders: {
      engravingInput: 'Gravurtext eingeben (max. 25 Zeichen)'
    },
    messages: {
      confirmRestart: 'Willst Du wirklich von vorne anfangen? Dein jetziges Set wird gelöscht.',
      noCaseSelected: 'Bitte wähle ein Holzkästchen aus.',
      noVariantSelected: 'Bitte wähle eine Variante für Slot %{slot} aus.',
      addToCartSuccess: 'Produkte wurden zum Warenkorb hinzugefügt!',
      addToCartModalMessage: 'Dein Set wurde in den Warenkorb gelegt und gespeichert.',
      addToCartError: 'Fehler beim Hinzufügen zum Warenkorb. Bitte versuche es erneut.',
      addToCartGenericError: 'Fehler beim Hinzufügen zum Warenkorb',
      fullscreenPromptTitle: 'Vollbild aktivieren?',
      fullscreenPromptMessage: 'Für die beste Ansicht kannst Du den Set-Konfigurator im Vollbild öffnen.',
      setPriceVariantMissing: 'Setpreis-Konfiguration fehlt oder ist unvollständig. Bitte hinterlege die Setpreis-IDs im Theme-Editor.',
      setPriceVariantNotFound: 'Setpreis-Variante %{id} wurde nicht gefunden. Bitte prüfe die Setpreis-IDs oder das Setpreis-Produkt.',
      setPriceVariantLoadError: 'Setpreis-Variante %{id} konnte nicht geladen werden. Bitte versuche es später erneut oder kontaktiere den Support.'
    },
    summary: {
      case: 'Holzkästchen',
      slot: 'Slot',
      sku: 'SKU',
      description: 'Beschreibung',
      empty: 'Nicht ausgewählt',
      engraving: 'Gravur',
      noText: 'Kein Text'
    },
    options: {
      engraveYes: 'Ja',
      engraveNo: 'Nein, lieber Standard-Markenlogo'
    },
    buttons: {
      prev: 'Zurück',
      next: 'Weiter',
      addToCart: 'In den Warenkorb',
      viewCart: 'Zum Warenkorb',
      newSet: 'Neues Set gestalten',
      fullscreenConfirm: 'Vollbild öffnen',
      fullscreenCancel: 'Später',
      restart: 'Von vorne anfangen',
      restartConfirm: 'Ja, von vorne anfangen',
      restartCancel: 'Abbrechen'
    }
  };

  const strings = {
    progress: { ...defaultStrings.progress, ...(window.setConfiguratorStrings?.progress || {}) },
    labels: { ...defaultStrings.labels, ...(window.setConfiguratorStrings?.labels || {}) },
    infoText: { ...defaultStrings.infoText, ...(window.setConfiguratorStrings?.infoText || {}) },
    placeholders: { ...defaultStrings.placeholders, ...(window.setConfiguratorStrings?.placeholders || {}) },
    messages: { ...defaultStrings.messages, ...(window.setConfiguratorStrings?.messages || {}) },
    summary: { ...defaultStrings.summary, ...(window.setConfiguratorStrings?.summary || {}) },
    options: { ...defaultStrings.options, ...(window.setConfiguratorStrings?.options || {}) },
    buttons: { ...defaultStrings.buttons, ...(window.setConfiguratorStrings?.buttons || {}) }
  };

  const engraveYes = strings.options.engraveYes || 'Ja';
  const engraveNo = strings.options.engraveNo || 'Nein';
  const ENGRAVING_FLAG_TRUE = 'true';
  const ENGRAVING_FLAG_FALSE = 'false';
  const DEFAULT_ENGRAVING_TEXT = isGermanLocale ? 'Bester Papa' : 'Best Dad';
  const DEFAULT_SLOT_SELECTIONS = {
    type: isGermanLocale ? 'Buckelsmesser' : 'Breakfast Knife',
    blade: isGermanLocale ? 'Wellenschliff' : 'Serrated edge',
    handle: isGermanLocale ? 'Walnuss' : 'Walnut',
    engrave: ENGRAVING_FLAG_TRUE
  };

  const isEngravingEnabled = (value) => String(value) === ENGRAVING_FLAG_TRUE;

  const hasAnyEngravingSelected = () => {
    const slotSteps = Array.from(document.querySelectorAll('.wizard-step[data-step]')).filter((step) => {
      const index = Number(step.dataset.step);
      return Number.isFinite(index) && index >= 1 && index <= state.totalSlots;
    });

    return slotSteps.some((step) => {
      const engravingFlag = step.dataset.engrave;
      const engravingChosen = isEngravingEnabled(engravingFlag);

      return engravingChosen;
    });
  };

  const resolveSetPriceVariantId = () => {
    const hasEngraving = hasAnyEngravingSelected();
    const setPriceMapping = SET_PRICE_VARIANTS[state.totalSlots] || null;

    if (!setPriceMapping) {
      return { variantId: null, setPriceMapping: null };
    }

    const variantId = hasEngraving ? setPriceMapping.withEngraving : setPriceMapping.noEngraving;

    return { variantId, setPriceMapping };
  };

  const engravingPlaceholder = strings.placeholders.engravingInput || 'Gravurtext eingeben (max. 25 Zeichen)';

  const infoText = strings.infoText;

  const labelFallback = {
    'data-type': '1. Messertyp',
    'data-blade': '2. Klinge',
    'data-handle': '3. Griff',
    'data-engrave': '4. Personalisierte Lasergravur'
  };

  const DEFAULT_SLOT_LAYOUT = {
    positions: [-0.05, 0.115, 0.275, 0.435, 0.595, 0.755],
    adjustments: {
      desktop: {},
      mobile: {}
    }
  };

  const TWO_SLOT_LAYOUT = {
    positions: [0.02, 0.34],
    adjustments: {
      desktop: {
        2: [-40, -8]
      },
      mobile: {
        2: [-18, -2]
      }
    }
  };

  const FOUR_SLOT_LAYOUT = {
    positions: [...DEFAULT_SLOT_LAYOUT.positions],
    adjustments: {
      desktop: {
        4: [-12, 25, 68, 105]
      },
      mobile: {
        4: [-5, 10, 25, 45]
      }
    }
  };

  const SIX_SLOT_LAYOUT = {
    positions: [...DEFAULT_SLOT_LAYOUT.positions],
    adjustments: {
      desktop: {
        6: [0, -3, 0, 0, 0, 5]
      },
      mobile: {
        6: [0, 0, 0, 0, 0, 0]
      }
    }
  };

  const CASE_SLOT_LAYOUTS = {
    ids: {
      '56686566572297': { ...TWO_SLOT_LAYOUT },
      '56686566605065': { ...FOUR_SLOT_LAYOUT },
      '56686566637833': { ...SIX_SLOT_LAYOUT },
      '56649622290697': { ...TWO_SLOT_LAYOUT },
      '56649622323465': { ...FOUR_SLOT_LAYOUT },
      '56649622356233': { ...SIX_SLOT_LAYOUT },
      '55259801518345': { ...TWO_SLOT_LAYOUT },
      '55259801551113': { ...FOUR_SLOT_LAYOUT },
      '55259801583881': { ...SIX_SLOT_LAYOUT }
    },
    skus: {
      'CASE.0002.CUSTOM': { ...TWO_SLOT_LAYOUT },
      'CASE.0004.CUSTOM': { ...FOUR_SLOT_LAYOUT },
      'CASE.0006.CUSTOM': { ...SIX_SLOT_LAYOUT }
    },
    slots: {
      2: { ...TWO_SLOT_LAYOUT },
      4: { ...FOUR_SLOT_LAYOUT },
      6: { ...SIX_SLOT_LAYOUT }
    }
  };

  function registerLiquidCaseLayouts() {
    const layoutEntries = [
      { id: CASE_VARIANT_IDS.two, slots: 2 },
      { id: CASE_VARIANT_IDS.four, slots: 4 },
      { id: CASE_VARIANT_IDS.six, slots: 6 }
    ];

    layoutEntries.forEach(({ id, slots }) => {
      if (!id) return;
      const layout = CASE_SLOT_LAYOUTS.slots[slots] || DEFAULT_SLOT_LAYOUT;
      CASE_SLOT_LAYOUTS.ids[id] = layout;
    });
  }

  registerLiquidCaseLayouts();

  function getSlotLayoutForCase(caseId, caseSku, slotCount = state.totalSlots) {
    const layout = CASE_SLOT_LAYOUTS.ids[caseId]
      || CASE_SLOT_LAYOUTS.skus[caseSku]
      || CASE_SLOT_LAYOUTS.slots[slotCount]
      || DEFAULT_SLOT_LAYOUT;

    return {
      positions: Array.isArray(layout.positions) ? layout.positions : DEFAULT_SLOT_LAYOUT.positions,
      adjustments: {
        desktop: layout.adjustments?.desktop || DEFAULT_SLOT_LAYOUT.adjustments.desktop,
        mobile: layout.adjustments?.mobile || DEFAULT_SLOT_LAYOUT.adjustments.mobile
      }
    };
  }

  const formatString = (template = '', replacements = {}) => {
    return Object.keys(replacements).reduce((acc, key) => {
      return acc.replace(new RegExp(`%{${key}}`, 'g'), replacements[key]);
    }, template);
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;

    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  let previewRenderScheduled = false;
  const schedulePreviewRender = () => {
    if (previewRenderScheduled) return;
    previewRenderScheduled = true;

    const runRender = () => {
      previewRenderScheduled = false;
      PreviewModule.drawGlobal();
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(runRender);
      return;
    }

    window.setTimeout(runRender, 50);
  };

  const imageCache = new Map();
  const loadImage = (src) => {
    if (!src) return Promise.reject(new Error('Kein Bildpfad angegeben'));

    const cached = imageCache.get(src);
    if (cached) return cached;

    const imgPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = PREVIEW_CROSS_ORIGIN;
      img.onload = () => resolve(img);
      img.onerror = () => {
        imageCache.delete(src);
        reject(new Error(`Bild konnte nicht geladen werden: ${src}`));
      };
      img.src = src;
    });

    imageCache.set(src, imgPromise);
    return imgPromise;
  };

  const PREFETCH_CONCURRENCY = 3;
  const PREFETCH_IDLE_TIMEOUT = 1200;
  const prefetchQueue = [];
  const prefetchQueuedSrcs = new Set();
  let prefetchActiveCount = 0;

  const scheduleIdlePrefetch = (callback) => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(callback, { timeout: PREFETCH_IDLE_TIMEOUT });
      return;
    }

    window.setTimeout(callback, 200);
  };

  const drainPrefetchQueue = () => {
    while (prefetchActiveCount < PREFETCH_CONCURRENCY && prefetchQueue.length > 0) {
      const src = prefetchQueue.shift();
      if (!src) continue;
      prefetchActiveCount += 1;
      loadImage(src)
        .catch(error => {
          console.warn('Prefetch Bild fehlgeschlagen:', error);
        })
        .finally(() => {
          prefetchActiveCount -= 1;
          drainPrefetchQueue();
        });
    }
  };

  const enqueuePrefetchImage = (src) => {
    if (!src || prefetchQueuedSrcs.has(src)) return;
    prefetchQueuedSrcs.add(src);
    prefetchQueue.push(src);
    drainPrefetchQueue();
  };

  const prefetchVariantImages = (variants) => {
    if (!Array.isArray(variants) || variants.length === 0) return;

    scheduleIdlePrefetch(() => {
      variants.forEach(variant => {
        if (!variant || variant._prefetchQueued) return;
        variant._prefetchQueued = true;
        [variant.standard, variant.preview].filter(Boolean).forEach(enqueuePrefetchImage);
      });
    });
  };

  const DEFAULT_ENGRAVING_SETTINGS = {
    xFactor: 0.31,
    yFactor: 0.56,
    fontDesktop: 26,
    fontMobile: 14
  };

  const DEFAULT_STEAK_ENGRAVING_SETTINGS = {
    xFactor: 0.36,
    yFactor: 0.52
  };

  const ENGRAVING_FONT_SCALE = 1.1;
  const ENGRAVING_MOBILE_LETTER_SPACING_FACTOR = -0.04;

  async function fetchVariantPriceCents(variantId) {
    const cacheKey = String(variantId);
    if (!variantId) return null;
    if (priceCache.has(cacheKey)) {
      return Promise.resolve(priceCache.get(cacheKey));
    }

    const fetchPromise = fetch(`/variants/${variantId}.js`)
      .then(response => {
        if (!response.ok) throw new Error(`Preis für Variante ${variantId} konnte nicht geladen werden`);
        return response.json();
      })
      .then(data => {
        const cents = Number(data?.price);
        const normalized = Number.isFinite(cents) ? cents : null;
        priceCache.set(cacheKey, normalized);
        return normalized;
      })
      .catch(error => {
        console.warn('Preisabruf fehlgeschlagen:', error);
        priceCache.set(cacheKey, null);
        return null;
      });

    priceCache.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  async function findPriceCentsById(id, { fetchIfMissing = false } = {}) {
    if (!id) return null;

    const cacheKey = String(id);
    const match = window.konfiguratorProdukte?.find?.(p => String(p.id) === cacheKey);
    if (match && Number.isFinite(match.priceCents)) {
      priceCache.set(cacheKey, match.priceCents);
      return match.priceCents;
    }

    const cached = priceCache.get(cacheKey);
    if (typeof cached === 'number') return cached;

    if (cached && typeof cached.then === 'function') {
      const resolved = await cached;
      return Number.isFinite(resolved) ? resolved : null;
    }

    if (fetchIfMissing) {
      const resolved = await fetchVariantPriceCents(id);
      return Number.isFinite(resolved) ? resolved : null;
    }

    return null;
  }

  async function fetchVariantPriceWithValidation(variantId) {
    if (!variantId) {
      return {
        priceCents: null,
        error: strings.messages.setPriceVariantMissing || defaultStrings.messages.setPriceVariantMissing
      };
    }

    const cacheKey = String(variantId);
    const cached = priceCache.get(cacheKey);
    if (typeof cached === 'number') {
      return { priceCents: cached, error: null };
    }
    if (cached && typeof cached.then === 'function') {
      const resolved = await cached;
      if (typeof resolved === 'number') {
        return { priceCents: resolved, error: null };
      }
    }

    try {
      const response = await fetch(`/variants/${variantId}.js`);

      if (response.status === 404) {
        return {
          priceCents: null,
          error: formatString(strings.messages.setPriceVariantNotFound, { id: variantId })
        };
      }

      if (!response.ok) {
        return {
          priceCents: null,
          error: formatString(strings.messages.setPriceVariantLoadError, { id: variantId })
        };
      }

      const data = await response.json();
      const cents = Number(data?.price);
      const normalized = Number.isFinite(cents) ? cents : null;
      priceCache.set(cacheKey, normalized);

      if (!Number.isFinite(normalized)) {
        return {
          priceCents: null,
          error: formatString(strings.messages.setPriceVariantLoadError, { id: variantId })
        };
      }

      return { priceCents: normalized, error: null };
    } catch (err) {
      return {
        priceCents: null,
        error: formatString(strings.messages.setPriceVariantLoadError, { id: variantId })
      };
    }
  }

  function formatMoneyFromCents(cents = 0) {
    const value = Number.isFinite(cents) ? cents : 0;
    return moneyFormatter.format(value / 100);
  }

  function stringOrEmpty(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number' && !Number.isFinite(value)) return '';
    return String(value);
  }

  function toBase64Url(binary) {
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function fromBase64Url(value) {
    const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
    const remainder = normalized.length % 4;
    return atob(remainder ? normalized + '='.repeat(4 - remainder) : normalized);
  }

  // v2: base64url der UTF-8-Bytes. v1 hat den JSON vorher noch percent-encodiert
  // (jedes {, ", : wurde zu 3 Zeichen) — das hat den State grundlos ~3x so lang gemacht.
  function encodeStatePayload(payload) {
    try {
      const json = JSON.stringify(payload || {});
      const bytes = new TextEncoder().encode(json);
      let binary = '';

      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      return `v2:${toBase64Url(binary)}`;
    } catch (error) {
      console.warn('Set-Konfigurator: State konnte nicht encodiert werden', error);
      return '';
    }
  }

  function decodeStatePayload(encoded) {
    if (!encoded) return null;

    const isV2 = encoded.startsWith('v2:');
    const normalized = isV2 || encoded.startsWith('v1:') ? encoded.slice(3) : encoded;

    try {
      if (isV2) {
        const binary = fromBase64Url(normalized);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        return JSON.parse(new TextDecoder().decode(bytes));
      }

      // v1-Fallback für alte Warenkorb-Items und bereits verschickte Bearbeiten-Links
      return JSON.parse(decodeURIComponent(atob(normalized)));
    } catch (error) {
      console.warn('Set-Konfigurator: State konnte nicht decodiert werden', error);
      return null;
    }
  }

  function storeSetPreviewLocally(setId, dataUrl) {
    if (!setId || !dataUrl) return;

    try {
      const store = window.localStorage;
      if (!store) return;

      const keys = [];

      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && key.startsWith(SET_PREVIEW_STORAGE_PREFIX)) keys.push(key);
      }

      // Cache begrenzen, damit der localStorage nicht mit alten Vorschauen volläuft
      while (keys.length >= SET_PREVIEW_STORAGE_LIMIT) {
        store.removeItem(keys.shift());
      }

      store.setItem(`${SET_PREVIEW_STORAGE_PREFIX}${setId}`, dataUrl);
    } catch (error) {
      console.warn('Set-Konfigurator: Vorschau konnte nicht lokal gespeichert werden', error);
    }
  }

  function resolveSkuWithEngraving(rawSku, engravingEnabled) {
    if (!rawSku) return '';

    const normalizedSku = String(rawSku).trim();
    if (!engravingEnabled) return normalizedSku;

    return normalizedSku.endsWith(CUSTOM_SKU_SUFFIX)
      ? normalizedSku
      : `${normalizedSku}${CUSTOM_SKU_SUFFIX}`;
  }

  function sanitizeProperties(properties = {}) {
    const cleaned = {};
    const blockedPropertyKeys = new Set([
      '_bundle_price_cents',
      '_preis_authority_zielpreis_cent',
      '_preis_authority_zielpreis',
      '_preis_authority_grundlage',
      '_preis_authority_warenkorbsumme_cent',
      '_preis_authority_warenkorbsumme',
      '_preis_authority_verifikation',
      '_preis_authority_verfikikation'
    ]);

    for (const [key, value] of Object.entries(properties)) {
      if (blockedPropertyKeys.has(key)) {
        continue;
      }

      cleaned[key] = stringOrEmpty(value);
    }

    return cleaned;
  }

  function normalizeVariantId(variantId) {
    const parsedId = Number(variantId);
    return Number.isFinite(parsedId) ? parsedId : null;
  }

  function collectSlotState(step) {
    if (!step) return null;

    const engravingInput = step.querySelector('.engraving-input');
    const engravingText = engravingInput?.value.trim() || '';

    return {
      i: stringOrEmpty(step.dataset.selectedId),
      s: stringOrEmpty(step.dataset.sku),
      t: stringOrEmpty(step.dataset.type),
      b: stringOrEmpty(step.dataset.blade),
      h: stringOrEmpty(step.dataset.handle),
      g: {
        e: isEngravingEnabled(step.dataset.engrave),
        t: engravingText
      }
    };
  }

  function buildCompactStateObject() {
    if (!state.selectedCaseId || !state.totalSlots) return null;

    const slots = [];

    for (let i = 1; i <= state.totalSlots; i++) {
      const slotState = collectSlotState(state.steps[i]);
      if (slotState) {
        slots.push(slotState);
      }
    }

    return {
      v: 1,
      c: {
        i: stringOrEmpty(state.selectedCaseId),
        s: stringOrEmpty(state.selectedCaseSku),
        n: stringOrEmpty(state.selectedCaseName),
        t: state.totalSlots
      },
      s: slots
    };
  }

  function buildEncodedState() {
    const payload = buildCompactStateObject();
    if (!payload) return '';

    return encodeStatePayload(payload);
  }

  /* =========================================
     Module: DOM Selectors
     ========================================= */
  const DOM = {
    wizardContainer: document.getElementById('wizard-container'),
    previewCanvas:   document.getElementById('set-preview-canvas'),
    prevBtn:         document.getElementById('prev-step'),
    restartBtn:      document.getElementById('restart-configurator'),
    nextBtn:         document.getElementById('next-step'),
    cartBtn:         document.getElementById('add-to-cart'),
    cartPrice:       document.getElementById('add-to-cart-price'),
    summaryDiv:      document.getElementById('set-summary'),
    progressBar:     document.getElementById('wizard-progress'),
    progressSteps:   document.getElementById('wizard-progress-steps'),
    wrapper:         document.getElementById('set-konfigurator-wrapper'),
    wizardPanel:     document.querySelector('.wizard-panel'),
    header:          document.querySelector('.set-konfigurator-header'),
    cartModal:       document.getElementById('set-konfigurator-cart-modal'),
    cartModalDialog: document.querySelector('#set-konfigurator-cart-modal .set-konfigurator-cart-modal__dialog'),
    cartModalMessage: document.getElementById('set-konfigurator-cart-modal-message'),
    cartModalViewCart: document.getElementById('set-konfigurator-cart-modal-view-cart'),
    cartModalNewSet: document.getElementById('set-konfigurator-cart-modal-new-set'),
    restartModal: document.getElementById('set-konfigurator-restart-modal'),
    restartModalDialog: document.querySelector('#set-konfigurator-restart-modal .set-konfigurator-cart-modal__dialog'),
    restartModalMessage: document.getElementById('set-konfigurator-restart-modal-message'),
    restartModalConfirm: document.getElementById('set-konfigurator-restart-modal-confirm'),
    restartModalCancel: document.getElementById('set-konfigurator-restart-modal-cancel'),
    fullscreenModal: document.getElementById('set-konfigurator-fullscreen-modal'),
    fullscreenModalDialog: document.querySelector('#set-konfigurator-fullscreen-modal .set-konfigurator-fullscreen-modal__dialog'),
    fullscreenModalTitle: document.getElementById('set-konfigurator-fullscreen-modal-title'),
    fullscreenModalMessage: document.getElementById('set-konfigurator-fullscreen-modal-message'),
    fullscreenModalConfirm: document.getElementById('set-konfigurator-fullscreen-modal-confirm'),
    fullscreenModalCancel: document.getElementById('set-konfigurator-fullscreen-modal-cancel')
  };

  const ctx = DOM.previewCanvas.getContext('2d');
  const bgImg = new Image();
  bgImg.crossOrigin = PREVIEW_CROSS_ORIGIN;

  if (DOM.prevBtn) DOM.prevBtn.textContent = strings.buttons.prev;
  if (DOM.restartBtn) DOM.restartBtn.textContent = strings.buttons.restart;
  if (DOM.nextBtn) DOM.nextBtn.textContent = strings.buttons.next;
  if (DOM.cartBtn) {
    const cartLabel = DOM.cartBtn.querySelector('.add-to-cart-label');
    if (cartLabel) {
      cartLabel.textContent = strings.buttons.addToCart;
    } else {
      DOM.cartBtn.textContent = strings.buttons.addToCart;
    }
  }
  if (DOM.cartModalMessage && strings.messages.addToCartModalMessage) {
    DOM.cartModalMessage.textContent = strings.messages.addToCartModalMessage;
  }
  if (DOM.restartModalMessage && strings.messages.confirmRestart) {
    DOM.restartModalMessage.textContent = strings.messages.confirmRestart;
  }
  if (DOM.cartModalViewCart && strings.buttons.viewCart) {
    DOM.cartModalViewCart.textContent = strings.buttons.viewCart;
  }
  if (DOM.cartModalNewSet && strings.buttons.newSet) {
    DOM.cartModalNewSet.textContent = strings.buttons.newSet;
  }
  if (DOM.fullscreenModalTitle && strings.messages.fullscreenPromptTitle) {
    DOM.fullscreenModalTitle.textContent = strings.messages.fullscreenPromptTitle;
  }
  if (DOM.fullscreenModalMessage && strings.messages.fullscreenPromptMessage) {
    DOM.fullscreenModalMessage.textContent = strings.messages.fullscreenPromptMessage;
  }
  if (DOM.fullscreenModalConfirm && strings.buttons.fullscreenConfirm) {
    DOM.fullscreenModalConfirm.textContent = strings.buttons.fullscreenConfirm;
  }
  if (DOM.fullscreenModalCancel && strings.buttons.fullscreenCancel) {
    DOM.fullscreenModalCancel.textContent = strings.buttons.fullscreenCancel;
  }
  if (DOM.restartModalConfirm && strings.buttons.restartConfirm) {
    DOM.restartModalConfirm.textContent = strings.buttons.restartConfirm;
  }
  if (DOM.restartModalCancel && strings.buttons.restartCancel) {
    DOM.restartModalCancel.textContent = strings.buttons.restartCancel;
  }

  function updateStickyOffsets() {
    if (!DOM.wrapper || !DOM.progressBar) return;
    const progressStyles = window.getComputedStyle(DOM.progressBar);
    const marginBottom = parseFloat(progressStyles.marginBottom) || 0;
    const progressHeight = DOM.progressBar.getBoundingClientRect().height;
    const offset = Math.round(progressHeight + marginBottom);
    DOM.wrapper.style.setProperty('--set-progress-offset', `${offset}px`);
  }

  function setupStickyOffsets() {
    if (!DOM.wrapper || !DOM.progressBar) return;
    updateStickyOffsets();
    window.addEventListener('resize', updateStickyOffsets, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => updateStickyOffsets());
      observer.observe(DOM.progressBar);
    }
  }

  function getPreviewCanvasPoint(event) {
    if (!DOM.previewCanvas) return null;
    const rect = DOM.previewCanvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const scaleX = DOM.previewCanvas.width / rect.width;
    const scaleY = DOM.previewCanvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }

  function getPreviewHitAreaAtPoint(x, y) {
    if (!Array.isArray(state.previewHitAreas)) return null;
    return state.previewHitAreas.find((area) => (
      x >= area.x
      && y >= area.y
      && x <= area.x + area.width
      && y <= area.y + area.height
    )) || null;
  }

  function setupPreviewHitAreaInteractions() {
    if (!DOM.previewCanvas) return;

    DOM.previewCanvas.addEventListener('click', (event) => {
      const point = getPreviewCanvasPoint(event);
      if (!point) return;
      const hit = getPreviewHitAreaAtPoint(point.x, point.y);
      if (!hit) return;
      if (hit.slotIndex < 1 || hit.slotIndex > state.totalSlots) return;
      NavigationModule.goTo(hit.slotIndex);
    });

    DOM.previewCanvas.addEventListener('mousemove', (event) => {
      const point = getPreviewCanvasPoint(event);
      if (!point) return;
      const hit = getPreviewHitAreaAtPoint(point.x, point.y);
      const shouldShowPointer = hit
        && hit.slotIndex >= 1
        && hit.slotIndex <= state.totalSlots;
      DOM.previewCanvas.style.cursor = shouldShowPointer ? 'pointer' : '';
    });

    DOM.previewCanvas.addEventListener('mouseleave', () => {
      DOM.previewCanvas.style.cursor = '';
    });
  }

  function getScrollContainer() {
    if (DOM.wrapper && DOM.wrapper.classList.contains('is-fullscreen')) {
      return DOM.wrapper;
    }
    return window;
  }

  function getScrollTop(container = null) {
    const resolvedContainer = container || getScrollContainer();
    return resolvedContainer === window ? (window.scrollY || 0) : (resolvedContainer.scrollTop || 0);
  }

  function initWizardPanelScroll() {
    if (!DOM.wrapper || !DOM.wizardPanel) return;
    const desktopQuery = window.matchMedia('(min-width: 960px)');
    const fadeSettings = {
      desktop: {
        fadeSize: 100,
        startOffset: 12
      },
      mobile: {
        fadeSize: 24,
        startOffset: 0
      }
    };
    let rafId = null;

    function getReferenceElement() {
      if (desktopQuery.matches) {
        return DOM.progressBar;
      }
      return document.querySelector('.set-preview');
    }

    function getFadeSettings() {
      return desktopQuery.matches ? fadeSettings.desktop : fadeSettings.mobile;
    }

    function updateFade() {
      rafId = null;
      const reference = getReferenceElement();
      if (!reference) {
        DOM.wizardPanel.classList.remove('is-fading');
        DOM.wizardPanel.style.setProperty('--wizard-fade-size', '0px');
        return;
      }

      const { fadeSize, startOffset } = getFadeSettings();
      const panelRect = DOM.wizardPanel.getBoundingClientRect();
      const referenceRect = reference.getBoundingClientRect();
      const overlap = Math.max(0, referenceRect.bottom - panelRect.top + startOffset);
      if (overlap > 0) {
        const fadeCut = overlap;
        DOM.wizardPanel.classList.add('is-fading');
        DOM.wizardPanel.style.setProperty('--wizard-fade-cut', `${fadeCut}px`);
        DOM.wizardPanel.style.setProperty('--wizard-fade-size', `${fadeSize}px`);
      } else {
        DOM.wizardPanel.classList.remove('is-fading');
        DOM.wizardPanel.style.setProperty('--wizard-fade-cut', '0px');
        DOM.wizardPanel.style.setProperty('--wizard-fade-size', '0px');
      }
    }

    function scheduleUpdate() {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(updateFade);
    }

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleUpdate, { passive: true });
    if (DOM.wrapper) {
      DOM.wrapper.addEventListener('scroll', scheduleUpdate, { passive: true });
    }
    desktopQuery.addEventListener('change', scheduleUpdate);

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => scheduleUpdate());
      observer.observe(DOM.wizardPanel);
      const reference = getReferenceElement();
      if (reference) {
        observer.observe(reference);
      }
    }

    scheduleUpdate();
  }

  /* =========================================
     Workaround für postMessage-Fehler
     ========================================= */
  (function() {
    const originalPostMessage = window.postMessage;
    window.postMessage = function(message, targetOrigin, transfer) {
      try {
        originalPostMessage.call(window, message, targetOrigin, transfer);
      } catch (e) {
        console.warn('postMessage Fehler ignoriert:', e.message, { message, targetOrigin });
      }
    };
    console.log('postMessage Workaround installiert'); // Debugging
  })();

  initWizardPanelScroll();

  /* =========================================
     Module: Fullscreen Prompt (Mobile)
     ========================================= */
  function requestFullscreen(element) {
    if (!element) return Promise.resolve();
    const fullscreenRequest =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;
    if (!fullscreenRequest) return Promise.resolve();
    try {
      const result = fullscreenRequest.call(element);
      if (result && typeof result.then === 'function') {
        return result.catch((error) => {
          console.warn('Vollbild konnte nicht aktiviert werden:', error);
        });
      }
      return Promise.resolve();
    } catch (error) {
      console.warn('Vollbild konnte nicht aktiviert werden:', error);
      return Promise.resolve();
    }
  }

  function openFullscreenModal() {
    if (!DOM.fullscreenModal || !DOM.fullscreenModalDialog) return;
    DOM.fullscreenModal.classList.add('is-open');
    DOM.fullscreenModal.setAttribute('aria-hidden', 'false');
    DOM.fullscreenModalDialog.focus();
  }

  function closeFullscreenModal() {
    if (!DOM.fullscreenModal) return;
    DOM.fullscreenModal.classList.remove('is-open');
    DOM.fullscreenModal.setAttribute('aria-hidden', 'true');
  }

  function setupFullscreenPrompt() {
    if (!DOM.fullscreenModal || !DOM.fullscreenModalConfirm) return;
    if (!window.matchMedia || !window.matchMedia(MOBILE_FULLSCREEN_QUERY).matches) return;
    openFullscreenModal();

    DOM.fullscreenModal.addEventListener('click', (event) => {
      if (event.target.matches('[data-fullscreen-modal-close]')) {
        closeFullscreenModal();
      }
    });

    DOM.fullscreenModalConfirm.addEventListener('click', () => {
      const target = DOM.wrapper || document.documentElement;
      requestFullscreen(target).finally(() => closeFullscreenModal());
    });

    if (DOM.fullscreenModalCancel) {
      DOM.fullscreenModalCancel.addEventListener('click', closeFullscreenModal);
    }
  }

  function updateFullscreenState() {
    if (!DOM.wrapper) return;
    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;
    const isWrapperFullscreen = fullscreenElement === DOM.wrapper;
    DOM.wrapper.classList.toggle('is-fullscreen', isWrapperFullscreen);
  }

  document.addEventListener('fullscreenchange', updateFullscreenState);
  document.addEventListener('webkitfullscreenchange', updateFullscreenState);
  document.addEventListener('mozfullscreenchange', updateFullscreenState);
  document.addEventListener('MSFullscreenChange', updateFullscreenState);

  setupFullscreenPrompt();

  /* =========================================
     Module: Info Modal
     ========================================= */
  function normalizeInfoHtml(text) {
    if (!text) return '';
    return text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function showInfo(text) {
    const normalizedText = normalizeInfoHtml(text);
    const overlay = document.createElement('div');
    overlay.className = 'info-modal-overlay';
    overlay.innerHTML = `
      <div class="info-modal">
        <span class="close-modal">×</span>
        <div class="info-modal__content">${normalizedText}</div>
      </div>
    `;
    overlay.querySelector('.close-modal').onclick = () => overlay.remove();
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        overlay.remove();
      }
    });
    document.body.append(overlay);
  }

  /* =========================================
     Module: Cart Modal
     ========================================= */
  function openCartModal() {
    if (!DOM.cartModal) return false;
    DOM.cartModal.classList.add('is-open');
    DOM.cartModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    DOM.cartModalDialog?.focus?.();
    return true;
  }

  function closeCartModal() {
    if (!DOM.cartModal) return;
    DOM.cartModal.classList.remove('is-open');
    DOM.cartModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* =========================================
     Module: Restart Modal
     ========================================= */
  function openRestartModal() {
    if (!DOM.restartModal) return false;
    DOM.restartModal.classList.add('is-open');
    DOM.restartModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    DOM.restartModalDialog?.focus?.();
    return true;
  }

  function closeRestartModal() {
    if (!DOM.restartModal) return;
    DOM.restartModal.classList.remove('is-open');
    DOM.restartModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function flashElement(el) {
    if (!el) return;
    el.classList.remove('flash-error');
    // Force reflow to restart animation
    void el.offsetWidth;
    el.classList.add('flash-error');
  }

  function isStepValid(stepIndex) {
    if (stepIndex === 0) {
      return !!state.selectedCaseId && state.selectedCaseId !== 'null';
    }

    if (stepIndex >= 1 && stepIndex <= state.totalSlots) {
      const step = state.steps[stepIndex];
      if (!step) return false;

      const hasVariant = !!step.dataset.selectedId;
      const engraveChoice = step.dataset.engrave === ENGRAVING_FLAG_TRUE || step.dataset.engrave === ENGRAVING_FLAG_FALSE;
      const needsText = isEngravingEnabled(step.dataset.engrave);
      const engravingText = step.querySelector('.engraving-input')?.value.trim();
      const hasText = !needsText || !!engravingText;

      return hasVariant && engraveChoice && hasText;
    }

    return true;
  }

  function highlightMissingForStep(stepIndex) {
    if (stepIndex === 0) {
      flashElement(document.querySelector('.case-options'));
      return;
    }

    const step = state.steps[stepIndex];
    if (!step) return;

    if (!step.dataset.type) flashElement(step.querySelector('[data-type]'));
    if (!step.dataset.blade) flashElement(step.querySelector('[data-blade]'));
    if (!step.dataset.handle) flashElement(step.querySelector('[data-handle]'));
    if (!step.dataset.engrave) flashElement(step.querySelector('[data-engrave]'));

    if (!step.dataset.selectedId && step.dataset.type && step.dataset.blade && step.dataset.handle) {
      flashElement(step);
    }

    if (isEngravingEnabled(step.dataset.engrave)) {
      const engravingInput = step.querySelector('.engraving-input');
      if (engravingInput && !engravingInput.value.trim()) {
        flashElement(engravingInput);
      }
    }
  }

  /* =========================================
     Module: Progress Leiste
     ========================================= */
  const ProgressModule = (function(){
    const mobileProgressQuery = window.matchMedia('(max-width: 767px)');

    function build() {
      if (!DOM.progressSteps) return;

      DOM.progressSteps.innerHTML = '';

      const items = [{ label: strings.progress.case, stepIndex: 0 }];
      const slotCount = Number(state.totalSlots) || 0;

      for (let i = 1; i <= slotCount; i++) {
        const slotLabel = (strings.progress.slot || 'Slot %i').replace('%i', i);
        items.push({ label: slotLabel, stepIndex: i });
      }

      items.push({ label: strings.progress.summary, stepIndex: slotCount + 1 });

      items.forEach(item => {
        const el = document.createElement('div');
        el.className = 'wizard-progress-item';
        el.dataset.stepIndex = item.stepIndex;
        el.innerHTML = `
          <div class="wizard-progress-dot">${item.stepIndex}</div>
          <span>${item.label}</span>
        `;
        el.addEventListener('click', () => {
          const targetIndex = parseInt(el.dataset.stepIndex, 10);
          if (targetIndex < state.currentStep && state.steps[targetIndex]) {
            const previousStep = Math.max(state.currentStep - 1, 0);
            NavigationModule.goTo(previousStep);
          }
        });
        DOM.progressSteps.appendChild(el);
      });

      updateActive();
      centerActiveItem();
      updateStickyOffsets();
    }

    function updateActive() {
      if (!DOM.progressSteps) return;

      DOM.progressSteps.querySelectorAll('.wizard-progress-item').forEach(el => {
        const idx = parseInt(el.dataset.stepIndex, 10);
        const isCaseStep = idx === 0;
        const isSummaryStep = idx === state.steps.length - 1;
        const isSlotStep = idx >= 1 && idx <= state.totalSlots;

        const isActive = idx === state.currentStep;
        const isCaseCompleted = isCaseStep && !!state.selectedCaseId;
        const isSlotCompleted = isSlotStep && isStepValid(idx);
        const isSummaryCompleted = isSummaryStep && state.currentStep === idx;
        const wasVisited = idx < state.currentStep;

        const isCompleted = !isActive && (isCaseCompleted || isSlotCompleted || isSummaryCompleted || wasVisited);

        el.classList.toggle('active', isActive);
        el.classList.toggle('completed', isCompleted);
      });

      centerActiveItem();
    }

    function centerActiveItem() {
      if (!DOM.progressSteps || !mobileProgressQuery.matches) return;

      const activeItem = DOM.progressSteps.querySelector('.wizard-progress-item.active');
      if (!activeItem) return;

      const container = DOM.progressSteps;
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const currentScroll = container.scrollLeft;
      const offsetLeft = itemRect.left - containerRect.left;
      const targetScroll = currentScroll + offsetLeft - (containerRect.width / 2 - itemRect.width / 2);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      container.scrollTo({
        left: targetScroll,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    return { build, updateActive };
  })();


/* =========================================
   Module: Step 0 – Case Selection
   ========================================= */
function initCaseSelection() {
  console.log('initCaseSelection gestartet'); // Debugging
  try {
    const wizardContainer = document.getElementById('wizard-container');
    if (!wizardContainer) {
      console.error('wizard-container nicht gefunden!');
      return;
    }

    const step0 = wizardContainer.querySelector('[data-step="0"]');
    const stepEnd = wizardContainer.querySelector('[data-step="end"]');
    if (!step0 || !stepEnd) {
      console.error('Step 0 oder End-Step nicht gefunden!', { step0, stepEnd });
      return;
    }

    const caseOptions = step0.querySelector('.case-options');
    if (!caseOptions) {
      console.error('case-options Container nicht gefunden!');
      return;
    }

    const caseButtons = caseOptions.querySelectorAll('.case-option');
    if (!caseButtons.length) {
      console.error('Keine .case-option Buttons gefunden!');
      return;
    }

    const caseSelectionNotice = document.getElementById('case-options-notice');
    const caseSelectionEnabled = caseOptions.dataset.enabled !== 'false' && CASE_SELECTION_ENABLED;
    if (caseSelectionNotice) {
      caseSelectionNotice.hidden = caseSelectionEnabled;
    }

    state.steps = [step0, stepEnd];
    ProgressModule.build();

    caseButtons.forEach(btn => {
      const hasVariantId = Boolean(btn.dataset.id);
      const shouldDisable = !caseSelectionEnabled || !hasVariantId;

      btn.classList.toggle('is-disabled', shouldDisable);
      btn.toggleAttribute('disabled', shouldDisable);
      btn.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

      btn.removeEventListener('click', handleCaseSelection);
      if (!shouldDisable) {
        btn.addEventListener('click', handleCaseSelection);
      }
    });

    if (!caseSelectionEnabled) {
      state.selectedCaseId = null;
      state.totalSlots = 0;
      NavigationModule.goTo(0);
      NavigationModule.updateNavButtons();
      return;
    }

    function handleCaseSelection(event) {
      try {
        const btn = event.currentTarget;

        const newSlotCount = parseInt(btn.dataset.slots, 10);

        step0.querySelectorAll('.case-option').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');

        state.selectedCaseId = btn.dataset.id;
        state.selectedCaseName = btn.querySelector('.case-title')?.textContent.trim() || 'Holzkästchen';
        state.selectedCaseSku = btn.dataset.sku || '';
        state.totalSlots = newSlotCount;
        state.casePricePromise = fetchVariantPriceCents(state.selectedCaseId)
          .then(price => {
            state.casePriceCents = price;
            return price;
          });

        // Slots erzeugen
        SlotModule.buildAll();

        // Schritte aktualisieren
        const allSteps = wizardContainer.querySelectorAll('.wizard-step[data-step]');
        state.steps = Array.from(allSteps);

        // Default-Auswahl für Slot 1
        const slot1 = state.steps[1];
        if (slot1) {
          applyDefaultSelections(slot1);

          const engravingInput = slot1.querySelector('.engraving-input');
          if (engravingInput) {
            engravingInput.value = DEFAULT_ENGRAVING_TEXT;
            engravingInput.dispatchEvent(new Event('input', { bubbles: true }));
            delete engravingInput.dataset.defaultCleared;
          }
        }

        // Bild setzen
        const img = btn.querySelector('img');
        if (img) {
          bgImg.src = img.src;

          // Warten auf Font UND Bild, dann rendern
          Promise.all([
            document.fonts.ready,
            new Promise(res => {
              bgImg.onload = res;
              bgImg.onerror = () => {
                console.error('Fehler beim Laden des Hintergrundbilds:', img.src);
                res();
              };
            })
          ]).then(() => {
            PreviewModule.drawGlobal();
          });
        }

        NavigationModule.goTo(1, { skipScroll: true });
      } catch (e) {
        console.error('Fehler in handleCaseSelection:', e);
      }
    }

    // Initial: Nur Schritt 0 und Ende
    NavigationModule.goTo(0);
  } catch (e) {
    console.error('Fehler in initCaseSelection:', e);
  }
  }




  function applyVariantToStep(step, variant) {
    if (!step) return;

    if (variant) {
      step.dataset.selectedId = variant.id;
      step.dataset.preview = variant.preview || '';
      step.dataset.sku = variant.sku || '';
      step.dataset.engravingX = variant.engravingX ?? '';
      step.dataset.engravingY = variant.engravingY ?? '';
      step.dataset.engravingXSteak = variant.engravingXSteak ?? '';
      step.dataset.engravingYSteak = variant.engravingYSteak ?? '';
      step.dataset.engravingFontDesktop = variant.engravingFontDesktop ?? '';
      step.dataset.engravingFontMobile = variant.engravingFontMobile ?? '';
      return;
    }

    delete step.dataset.selectedId;
    delete step.dataset.preview;
    delete step.dataset.sku;
    delete step.dataset.engravingX;
    delete step.dataset.engravingY;
    delete step.dataset.engravingXSteak;
    delete step.dataset.engravingYSteak;
    delete step.dataset.engravingFontDesktop;
    delete step.dataset.engravingFontMobile;
  }

  function selectButtonForValue(step, attr, value) {
    const wrapper = step?.querySelector(`[${attr}]`);
    if (!wrapper) return;

    const buttons = Array.from(wrapper.querySelectorAll('.filter-button'));
    const normalizedValue = String(value || '').trim();

    const targetButton = buttons.find((btn) => {
      const btnValue = btn.dataset.value || btn.textContent || '';
      if (attr === 'data-type') {
        return normalizeTypeKey(btnValue) === normalizeTypeKey(normalizedValue);
      }
      if (attr === 'data-blade') {
        return normalizeBladeKey(btnValue) === normalizeBladeKey(normalizedValue);
      }
      if (attr === 'data-handle') {
        return normalizeHandleKey(btnValue) === normalizeHandleKey(normalizedValue);
      }
      return String(btnValue || '').trim().toLowerCase() === normalizedValue.toLowerCase();
    });

    if (!targetButton) return;

    buttons.forEach((btn) => btn.classList.toggle('selected', btn === targetButton));
    targetButton.dispatchEvent(new Event('click', { bubbles: true }));
  }

  function applyDefaultSelections(step) {
    if (!step) return;
    if (step.dataset.defaultsApplied === 'true') return;

    const hasExistingSelection = Boolean(
      step.dataset.selectedId
      || step.dataset.type
      || step.dataset.blade
      || step.dataset.handle
    );

    if (hasExistingSelection) {
      step.dataset.defaultsApplied = 'true';
      return;
    }

    if (!step.dataset.type) {
      selectButtonForValue(step, 'data-type', DEFAULT_SLOT_SELECTIONS.type);
    }
    if (!step.dataset.blade) {
      selectButtonForValue(step, 'data-blade', DEFAULT_SLOT_SELECTIONS.blade);
    }
    if (!step.dataset.handle) {
      selectButtonForValue(step, 'data-handle', DEFAULT_SLOT_SELECTIONS.handle);
    }
    selectButtonForValue(step, 'data-engrave', DEFAULT_SLOT_SELECTIONS.engrave);

    step.dataset.defaultsApplied = 'true';
  }

  function applySlotStateFromPayload(slotIndex, slotState) {
    if (!slotState) return;

    const step = state.steps[slotIndex];
    if (!step) return;

    const normalizedVariantId = normalizeVariantId(slotState.i);
    const variant = window.konfiguratorProdukte.find((p) => String(p.id) === String(normalizedVariantId))
      || window.konfiguratorProdukte.find((p) => p.sku && p.sku === slotState.s);

    if (variant) {
      applyVariantToStep(step, variant);
    }

    selectButtonForValue(step, 'data-type', slotState.t);
    selectButtonForValue(step, 'data-blade', slotState.b);
    selectButtonForValue(step, 'data-handle', slotState.h);

    const engravingFlag = slotState.g?.e ? ENGRAVING_FLAG_TRUE : ENGRAVING_FLAG_FALSE;
    selectButtonForValue(step, 'data-engrave', engravingFlag);

    const engravingInput = step.querySelector('.engraving-input');
    if (engravingInput) {
      engravingInput.value = slotState.g?.t?.slice(0, ENGRAVING_MAX_LENGTH) || '';
      engravingInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  function applyPayloadToWizard(payload) {
    if (!payload?.c?.i) return;

    const targetCaseId = stringOrEmpty(payload.c.i);
    const caseButton = document.querySelector(`.case-option[data-id="${targetCaseId}"]`)
      || (payload.c.s ? document.querySelector(`.case-option[data-sku="${payload.c.s}"]`) : null);

    if (!caseButton) return;

    caseButton.click();

    requestAnimationFrame(() => {
      const slots = Array.isArray(payload.s) ? payload.s : [];
      slots.forEach((slotState, index) => applySlotStateFromPayload(index + 1, slotState));

      if (state.steps.length > 1) {
        NavigationModule.goTo(state.steps.length - 1);
        NavigationModule.updateNavButtons();
        PreviewModule.drawGlobal();
        if (CONFIGURATOR_SUMMARY_ANCHOR) {
          window.location.hash = CONFIGURATOR_SUMMARY_ANCHOR;
        }
      }
    });
  }

  function initializeFromQueryState() {
    const params = new URLSearchParams(window.location.search);
    const encodedState = params.get(SET_STATE_QUERY_PARAM) || params.get(SET_STATE_PROPERTY_KEY);

    const payload = decodeStatePayload(encodedState);
    if (!payload) return;

    applyPayloadToWizard(payload);
  }

  /* =========================================
     Module: Slot Builder
     ========================================= */
  const SlotModule = (function(){
  const normalizeLabel = (value) => (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const bladeAliases = {
    'with serration': 'wellenschliff',
    'without serration': 'glattschliff',
    'serrated edge': 'wellenschliff',
    serrated: 'wellenschliff',
    wellenschliff: 'wellenschliff',
    'smooth edge': 'glattschliff',
    'straight edge': 'glattschliff',
    straight: 'glattschliff',
    glattschliff: 'glattschliff'
  };

  const typeAliases = {
    'buckelsmesser fruhstucksmesser': 'buckelsmesser',
    'buckelsmesser breakfast knife': 'buckelsmesser',
    'buckels knife': 'buckelsmesser',
    'breakfast knife': 'buckelsmesser',
    'classic breakfast knife': 'buckelsmesser',
    'the classic breakfast knife': 'buckelsmesser',
    buckelsmesser: 'buckelsmesser',
    'steakmesser holzgriff': 'steakmesser',
    steakmesser: 'steakmesser',
    'steak knife': 'steakmesser'
  };

  const getCanonicalKey = (value, map) => map[normalizeLabel(value)] || normalizeLabel(value);
  const getTypeKey = (value) => {
    const normalized = normalizeLabel(value);
    if (typeAliases[normalized]) return typeAliases[normalized];
    if (normalized.includes('breakfast') || normalized.includes('buckels')) return 'buckelsmesser';
    if (normalized.includes('steak')) return 'steakmesser';
    return normalized;
  };

  const bladeIcons = {
    wellenschliff: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Wellenschliff2.png?v=1744809904',
    'serrated edge': 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Wellenschliff2.png?v=1744809904',
    serrated: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Wellenschliff2.png?v=1744809904',
    glattschliff: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Glattschliff_transparent.png?v=1745497761',
    'smooth edge': 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Glattschliff_transparent.png?v=1745497761',
    'straight edge': 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Glattschliff_transparent.png?v=1745497761',
    straight: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/Glattschliff_transparent.png?v=1745497761'
  };
  const handleIcons = {
    olive: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-fb18b271ba494075b88739906f9dc7ab-8333099827465-d5e9d40d-a091-4717-9dcc-883549fe858a.jpg?v=1744701863',
    walnuss: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-fb18b271ba494075b88739906f9dc7ab-8333069287689-f32c7072-61b8-4d4d-bdd7-2a34e16ac86e.jpg?v=1744701843',
    walnut: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-fb18b271ba494075b88739906f9dc7ab-8333069287689-f32c7072-61b8-4d4d-bdd7-2a34e16ac86e.jpg?v=1744701843',
    kirsche: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-griff-kirsche-79e2a85e-b9a7-4dba-b2e1-a54ac61511ad.jpg?v=1742283720',
    cherry: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-griff-kirsche-79e2a85e-b9a7-4dba-b2e1-a54ac61511ad.jpg?v=1742283720',
    padouk: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-griff-kirsche-ad76f9c3-90c2-42d0-880d-87ca7c3d2a68.png?v=1742283512',
    bloak: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-griff-bloak-e4c2c897-4229-44ae-a37c-85ceb67a7a58.png?v=1718359814',
    buche: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-griff-buche-b43df9e2-5399-4758-b567-2bd9bf88e501.png?v=1717158835',
    beech: 'https://cdn.shopify.com/s/files/1/0521/9313/2700/files/variant-wizard-griff-buche-b43df9e2-5399-4758-b567-2bd9bf88e501.png?v=1717158835'
  };

  const getIconForValue = (icons, value, aliasMap = {}) => icons[getCanonicalKey(value, aliasMap)] || icons[normalizeLabel(value)] || '';

  const splitTitleParts = (title) => {
    if (!title) return { type: '', blade: '', handle: '' };
    const separators = [' – ', ' — ', ' - '];
    let rawType = title;
    let rawDetail = '';

    for (const sep of separators) {
      const index = title.lastIndexOf(sep);
      if (index > -1) {
        rawType = title.slice(0, index);
        rawDetail = title.slice(index + sep.length);
        break;
      }
    }

    if (!rawDetail) {
      const parts = title.split(/[–—-]/).map(s => s.trim()).filter(Boolean);
      rawType = parts[0] || '';
      rawDetail = parts.slice(1).join(' ');
    }

    const detailParts = (rawDetail || '').split(/[\\/|]/).map(s => s.trim()).filter(Boolean);
    const blade = detailParts[0] || '';
    const handle = detailParts[1] || '';
    return { type: rawType.trim(), blade, handle };
  };

  function fillGroup(container, attr, values, icons) {
    const wrapper = container.querySelector(`[${attr}]`);
    wrapper.innerHTML = '';

    const lbl = document.createElement('p');
    lbl.className = 'filter-label';
    const labelKey = attr.replace('data-', '');
    const labelText = strings.labels[labelKey] || labelFallback[attr] || '';
    lbl.innerHTML = `${labelText} <span class="info-icon">ℹ</span>`;
    lbl.querySelector('.info-icon').onclick = () => showInfo(infoText[labelKey] || '');
    container.insertBefore(lbl, wrapper);

    if (attr === 'data-engrave') {
      const engravingInput = container.querySelector('.engraving-input');
      const engravingFlag = container.dataset.engrave === ENGRAVING_FLAG_TRUE
        ? ENGRAVING_FLAG_TRUE
        : ENGRAVING_FLAG_FALSE;
      container.dataset.engrave = engravingFlag;

        if (engravingInput) {
          engravingInput.style.display = 'block';
          container.insertBefore(wrapper, engravingInput.nextSibling);
          const clearDefaultEngraving = () => {
            if (engravingInput.dataset.defaultCleared === 'true') return;
            if (engravingInput.value.trim() !== DEFAULT_ENGRAVING_TEXT) return;
            engravingInput.value = '';
            engravingInput.dataset.defaultCleared = 'true';
            engravingInput.dispatchEvent(new Event('input', { bubbles: true }));
          };
          engravingInput.addEventListener('focus', clearDefaultEngraving);
          engravingInput.addEventListener('click', clearDefaultEngraving);
          engravingInput.addEventListener('input', () => {
            if (engravingInput.value.length > ENGRAVING_MAX_LENGTH) {
              engravingInput.value = engravingInput.value.slice(0, ENGRAVING_MAX_LENGTH);
              console.log(`Gravurtext auf ${ENGRAVING_MAX_LENGTH} Zeichen begrenzt`);
            }
            document.fonts.ready.then(() => schedulePreviewRender());
            NavigationModule.updateNavButtons();
          });
        }

      const btnYes = document.createElement('button');
      btnYes.className = 'filter-button';
      btnYes.dataset.value = ENGRAVING_FLAG_TRUE;
      btnYes.innerHTML = `<span>${engraveYes}</span>`;

      const btnNo = document.createElement('button');
      btnNo.className = 'filter-button';
      btnNo.dataset.value = ENGRAVING_FLAG_FALSE;
      btnNo.innerHTML = `<span>${engraveNo}</span>`;

      const setEngraveState = (value, shouldUpdate = true) => {
        const flag = value === ENGRAVING_FLAG_FALSE ? ENGRAVING_FLAG_FALSE : ENGRAVING_FLAG_TRUE;
        container.dataset.engrave = flag;
        const isEngraving = isEngravingEnabled(flag);

        btnYes.classList.toggle('selected', isEngraving);
        btnNo.classList.toggle('selected', !isEngraving);

        if (engravingInput) {
          engravingInput.style.display = isEngraving ? 'block' : 'none';
          if (isEngraving) {
            engravingInput.focus();
          }
        }

        if (shouldUpdate) {
          document.fonts.ready.then(() => schedulePreviewRender());
          NavigationModule.updateNavButtons();
        }
      };

      btnYes.addEventListener('click', () => setEngraveState(ENGRAVING_FLAG_TRUE));
      btnNo.addEventListener('click', () => setEngraveState(ENGRAVING_FLAG_FALSE));

      setEngraveState(container.dataset.engrave, false);
      wrapper.append(btnYes, btnNo);
      return;
    }

    values.forEach(v => {
      const btn = document.createElement('button');
      btn.className = 'filter-button';
      btn.dataset.value = v;
      const src = getIconForValue(icons, v, attr === 'data-blade' ? bladeAliases : {});
      btn.innerHTML = src
        ? `<img src="${src}" alt="${v}"><span>${v}</span>`
        : `<span>${v}</span>`;

      btn.addEventListener('click', () => {
        wrapper.querySelectorAll('.filter-button').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        container.dataset[attr.replace('data-', '')] = v;

        const { type, blade, handle } = container.dataset;
        if (type && blade && handle) {
          const typeKey = getTypeKey(type);
          const bladeKey = getCanonicalKey(blade, bladeAliases);
          const handleKey = normalizeHandleKey(handle);
          const variant = window.konfiguratorProdukte.find(p => {
            const { type: tt, blade: bb, handle: hh } = splitTitleParts(p.title);
            const ttKey = getTypeKey(tt);
            const bbKey = getCanonicalKey(bb, bladeAliases);
            const hhKey = normalizeHandleKey(hh);
            const typeMatch = ttKey === typeKey || ttKey.startsWith(typeKey) || typeKey.startsWith(ttKey);
            return typeMatch && bbKey === bladeKey && hhKey === handleKey;
          });
            if (variant) {
              applyVariantToStep(container, variant);
              if (!Number.isFinite(variant.priceCents)) {
                fetchVariantPriceCents(variant.id).then(price => {
                  if (Number.isFinite(price)) {
                    variant.priceCents = price;
                  }
                });
              }
            } else {
              applyVariantToStep(container, null);
            }
          }

        document.fonts.ready.then(() => schedulePreviewRender());
        NavigationModule.updateNavButtons();
      });

      wrapper.append(btn);
    });
  }

function buildAll() {
  const wizard = DOM.wizardContainer;

  // Vorherige Slot-Schritte entfernen
  wizard.querySelectorAll('.wizard-step[data-step]').forEach(s => {
    if (s.dataset.step !== '0' && s.dataset.step !== 'end') s.remove();
  });
  state.steps = state.steps.filter(s => s.dataset.step === '0' || s.dataset.step === 'end');

  // Slots hinzufügen
  for (let i = 1; i <= state.totalSlots; i++) {
    const div = document.createElement('div');
    div.className = 'wizard-step';
    div.dataset.step = i;
    div.innerHTML = `
      <div data-type class="icon-buttons"></div>
      <div data-blade="" class="icon-buttons"></div>
      <div data-handle class="icon-buttons"></div>
      <div data-engrave class="icon-buttons"></div>
      <input type="text" class="engraving-input" placeholder="${engravingPlaceholder}">
    `;
    wizard.insertBefore(div, wizard.querySelector('[data-step="end"]'));
    state.steps.splice(i, 0, div);

    fillGroup(div, 'data-type', extractValues('type'), {});
    fillGroup(div, 'data-blade', extractValues('blade'), bladeIcons);
    fillGroup(div, 'data-handle', extractValues('handle'), handleIcons);
    fillGroup(div, 'data-engrave', [engraveNo, engraveYes], {});
  }

  ProgressModule.build();

  PreviewModule.drawGlobal();
}


  function extractValues(key) {
    const values = new Set();
    window.konfiguratorProdukte.forEach(p => {
      if (String(p.id) === ENGRAVING_VARIANT_ID) return;
      const { type, blade, handle } = splitTitleParts(p.title);
    if (key === 'type') values.add(type);
      if (key === 'blade') values.add(blade);
      if (key === 'handle') values.add(handle);
    });

    let result = Array.from(values).filter(Boolean);

    if (key === 'type') {
      result = result
        .map((value) => {
          const keyName = getTypeKey(value);
          if (keyName === 'buckelsmesser') return isGermanLocale ? 'Buckelsmesser' : 'Breakfast Knife';
          if (keyName === 'steakmesser') return isGermanLocale ? 'Steakmesser' : 'Steak Knife';
          if (value === 'Buckelsmesser / Frühstücksmesser') return isGermanLocale ? 'Buckelsmesser' : 'Breakfast Knife';
          if (value === 'Steakmesser Holzgriff') return isGermanLocale ? 'Steakmesser' : 'Steak Knife';
          return value;
        })
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index);
    }

    if (key === 'blade') {
      result = result
        .map((value) => {
          const keyName = getCanonicalKey(value, bladeAliases);
          if (keyName === 'wellenschliff') return isGermanLocale ? 'Wellenschliff' : 'Serrated edge';
          if (keyName === 'glattschliff') return isGermanLocale ? 'Glattschliff' : 'Smooth edge';
          return '';
        })
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index);
    }

    if (key === 'handle') {
      const desiredOrder = ['Walnuss', 'Padouk', 'Olive', 'Kirsche', 'Bloak', 'Buche', 'Walnut', 'Cherry', 'Beech'];
      const ordered = desiredOrder.filter(value =>
        result.some(item => normalizeLabel(item) === normalizeLabel(value))
      );
      result = ordered.length > 0 ? ordered : result;
    }

    return result;
  }

  return { buildAll };
})();

  function getSlotVariants() {
    if (!Array.isArray(window.konfiguratorProdukte)) return [];
    return window.konfiguratorProdukte.filter(p => String(p.id) !== ENGRAVING_VARIANT_ID);
  }

  function prefetchVariantsForStep(stepIndex) {
    if (stepIndex < 1 || stepIndex > state.totalSlots) return;
    if (state.prefetchedSteps.has(stepIndex)) return;
    state.prefetchedSteps.add(stepIndex);
    prefetchVariantImages(getSlotVariants());
  }

/* =========================================
   Module: Preview Renderer
   ========================================= */
const PreviewModule = (function(){
    function drawEngravingText(ctx, text, x, y, { letterSpacing = 0 } = {}) {
        if (!text) return;
        if (!letterSpacing) {
            ctx.fillText(text, x, y);
            return;
        }

        const previousAlign = ctx.textAlign;
        ctx.textAlign = 'left';

        const characters = Array.from(text);
        const widths = characters.map(char => ctx.measureText(char).width);
        const totalWidth = widths.reduce((sum, width) => sum + width, 0)
            + letterSpacing * Math.max(0, characters.length - 1);
        let cursorX = x - (totalWidth / 2);

        characters.forEach((char, index) => {
            ctx.fillText(char, cursorX, y);
            cursorX += widths[index] + letterSpacing;
        });

        ctx.textAlign = previousAlign;
    }

    async function drawGlobal() {
        if (!state.selectedCaseId || !bgImg.complete) {
            state.previewHitAreas = [];
            return;
        }

        // Canvas-Größe anpassen
        const containerWidth = DOM.previewCanvas.parentElement.clientWidth;
        const isMobileViewport = window.innerWidth < 768;
        const shouldShrinkPreview = isMobileViewport && (state.totalSlots === 4 || state.totalSlots === 6);
        const previewScale = shouldShrinkPreview ? 0.85 : 1;
        const targetWidth = Math.max(1, Math.round(containerWidth * previewScale));
        const caseHeight = (bgImg.naturalHeight / bgImg.naturalWidth) * targetWidth;

        // Höhe anpassen: Platz für Holzkästchen und die aktuell relevanten Messer
        const isLastStep = state.currentStep === state.steps.length - 1;
        const configuredMaxSlot = (() => {
            let maxSlot = 0;
            for (let i = 1; i <= state.totalSlots; i++) {
                const stepDiv = state.steps[i];
                if (stepDiv?.dataset.selectedId) {
                    maxSlot = i;
                }
            }
            return maxSlot;
        })();
        const baseMaxSlotsToShow = isLastStep ? state.totalSlots : state.currentStep;
        const maxSlotsToShow = Math.max(baseMaxSlotsToShow, configuredMaxSlot);
        const { positions: slotPositions, adjustments } = getSlotLayoutForCase(state.selectedCaseId, state.selectedCaseSku, state.totalSlots);
        const slotAdjustments = isMobileViewport ? adjustments.mobile : adjustments.desktop;

        const getSlotY = (slotIndex) => {
            const baseFactor = slotPositions[slotIndex - 1] ?? slotPositions[slotPositions.length - 1];
            const baseY = caseHeight * baseFactor;
            const adjustment = slotAdjustments[state.totalSlots]?.[slotIndex - 1] ?? 0;
            return baseY + adjustment;
        };

        const isDesktop = window.innerWidth >= 768; // Desktop ab 768px
        const knifePromises = [];

        for (let i = 1; i <= maxSlotsToShow; i++) {
            const stepDiv = state.steps[i];
            if (!stepDiv) continue;

            const vid = stepDiv.dataset.selectedId;
            if (!vid) continue;
            const engr = isEngravingEnabled(stepDiv.dataset.engrave);
            const txt = stepDiv.querySelector('.engraving-input')?.value.trim();
            const variantData = window.konfiguratorProdukte.find(p => String(p.id) === vid);
            const src = engr
                ? (stepDiv.dataset.preview || variantData?.preview || variantData?.standard)
                : variantData?.standard;

            if (!src) continue;

            knifePromises.push(loadImage(src).then(img => {
                const ar = img.naturalWidth / img.naturalHeight;
                let baseWidth = targetWidth * 0.45;
                let baseHeight = baseWidth / ar;
                const normalizedType = (stepDiv.dataset.type || variantData?.title?.split('–')?.[0] || '').toLowerCase();
                const isSteakKnife = normalizedType.includes('steak');
                const isBuckelKnife = normalizedType.includes('buckel');
                const DEFAULT_KNIFE_SCALE = 2.6;
                const STEAK_KNIFE_SCALE = 2.6;
                const scale = isSteakKnife ? STEAK_KNIFE_SCALE : DEFAULT_KNIFE_SCALE; // Größe
                let knifeWidth = baseWidth * scale;
                let knifeHeight = baseHeight * scale;

                // Positionierung in den Aussparungen des Holzkästchens
                let x, y;
                if (i <= slotPositions.length) {
                    x = (targetWidth - knifeWidth) / 2;
                    y = getSlotY(i);
                } else {
                    // Für weitere Slots (falls mehr als 6), außerhalb des sichtbaren Bereichs
                    x = (targetWidth - knifeWidth) / 2;
                    y = caseHeight + (i - 2) * (knifeHeight + 20);
                }

                const engravingXFactor = isSteakKnife
                  ? parseNumber(stepDiv.dataset.engravingXSteak)
                    ?? parseNumber(variantData?.engravingXSteak)
                    ?? parseNumber(stepDiv.dataset.engravingX)
                    ?? parseNumber(variantData?.engravingX)
                    ?? DEFAULT_STEAK_ENGRAVING_SETTINGS.xFactor
                  : parseNumber(stepDiv.dataset.engravingX)
                    ?? parseNumber(variantData?.engravingX)
                    ?? DEFAULT_ENGRAVING_SETTINGS.xFactor;
                const engravingYFactor = isSteakKnife
                  ? parseNumber(stepDiv.dataset.engravingYSteak)
                    ?? parseNumber(variantData?.engravingYSteak)
                    ?? parseNumber(stepDiv.dataset.engravingY)
                    ?? parseNumber(variantData?.engravingY)
                    ?? DEFAULT_STEAK_ENGRAVING_SETTINGS.yFactor
                  : parseNumber(stepDiv.dataset.engravingY)
                    ?? parseNumber(variantData?.engravingY)
                    ?? DEFAULT_ENGRAVING_SETTINGS.yFactor;
                const engravingFontDesktop = parseNumber(stepDiv.dataset.engravingFontDesktop)
                  ?? parseNumber(variantData?.engravingFontDesktop)
                  ?? DEFAULT_ENGRAVING_SETTINGS.fontDesktop;
                const engravingFontMobile = parseNumber(stepDiv.dataset.engravingFontMobile)
                  ?? parseNumber(variantData?.engravingFontMobile)
                  ?? DEFAULT_ENGRAVING_SETTINGS.fontMobile;

                const engravingText = (engr && txt) ? txt : '';
                const baseFontSize = isDesktop ? engravingFontDesktop : engravingFontMobile;
                const mobileFontSizeScale = (!isDesktop && (isSteakKnife || isBuckelKnife)) ? 0.9 : 1;
                const fontSize = baseFontSize * mobileFontSizeScale;
                const scaledFontSize = fontSize * ENGRAVING_FONT_SCALE;

                return {
                    img,
                    slotIndex: i,
                    x,
                    y,
                    knifeWidth,
                    knifeHeight,
                    engravingText,
                    engravingXFactor,
                    engravingYFactor,
                    isSteakKnife,
                    isBuckelKnife,
                    fontSize,
                    scaledFontSize
                };
            }).catch((err) => {
                console.warn(err.message);
                return null;
            }));
        }

        const knifeResults = (await Promise.all(knifePromises)).filter(Boolean);
        const knivesBottomEdge = knifeResults.length
          ? Math.max(...knifeResults.map(({ y, knifeHeight }) => y + knifeHeight))
          : 0;
        const targetHeight = Math.max(caseHeight, knivesBottomEdge);
        const dpr = window.devicePixelRatio || 1;
        const canvasWidth = Math.round(targetWidth * dpr);
        const canvasHeight = Math.max(1, Math.round(targetHeight * dpr));
        const shouldResize = DOM.previewCanvas.width !== canvasWidth
          || DOM.previewCanvas.height !== canvasHeight;

        if (shouldResize) {
            DOM.previewCanvas.width = canvasWidth;
            DOM.previewCanvas.height = canvasHeight;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        } else {
            ctx.clearRect(0, 0, targetWidth, targetHeight);
        }

        DOM.previewCanvas.style.width = `${targetWidth}px`;
        DOM.previewCanvas.style.height = `${targetHeight}px`;

        state.previewHitAreas = knifeResults.map(({ slotIndex, x, y, knifeWidth, knifeHeight }) => ({
            slotIndex,
            x: x * dpr,
            y: y * dpr,
            width: knifeWidth * dpr,
            height: knifeHeight * dpr
        }));

        // Hintergrundbild (Holzkästchen) zeichnen
        ctx.drawImage(bgImg, 0, 0, targetWidth, caseHeight);

        // Messer zeichnen
        knifeResults.forEach(({ img, x, y, knifeWidth, knifeHeight }) => {
            ctx.drawImage(img, x, y, knifeWidth, knifeHeight);
        });

        // Gravurtexte in separatem Durchlauf über die Messer legen
        knifeResults.filter(result => result.engravingText).forEach(({
            engravingText,
            engravingXFactor,
            engravingYFactor,
            isSteakKnife,
            isBuckelKnife,
            fontSize,
            x,
            y,
            knifeWidth,
            knifeHeight,
            scaledFontSize
        }) => {
            const activeFontSize = scaledFontSize;

            ctx.font = `${activeFontSize}px 'Allura'`; // Desktop : Mobil
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
            ctx.shadowBlur = 4;
            const textX = x + knifeWidth * engravingXFactor; // Position X gleich für beide
            const textY = y + knifeHeight * engravingYFactor;   // Gravurtext etwas weiter unten
            const letterSpacingFactor = (!isDesktop && (isSteakKnife || isBuckelKnife))
              ? ENGRAVING_MOBILE_LETTER_SPACING_FACTOR * 0.75
              : ENGRAVING_MOBILE_LETTER_SPACING_FACTOR;
            const letterSpacing = isDesktop ? 0 : activeFontSize * letterSpacingFactor;
            drawEngravingText(ctx, engravingText, textX, textY, { letterSpacing });
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
        });

        updatePreviewDataUrl();
    }

    function updatePreviewDataUrl() {
        try {
            const dataUrl = DOM.previewCanvas.toDataURL('image/png');
            state.previewDataUrl = dataUrl;
            return dataUrl;
        } catch (error) {
            console.warn('Set-Vorschau konnte nicht als Data-URL erzeugt werden:', error);
            return state.previewDataUrl || '';
        }
    }

    function getPreviewDataUrl() {
        if (state.previewDataUrl) return state.previewDataUrl;
        return updatePreviewDataUrl();
    }

    function getPreviewThumbnailDataUrl({ maxWidth = 300, mimeType = 'image/png', quality = 0.7 } = {}) {
        try {
            const sourceCanvas = DOM.previewCanvas;
            if (!sourceCanvas || !sourceCanvas.width || !sourceCanvas.height) return '';

            const scale = Math.min(1, maxWidth / sourceCanvas.width);
            const targetWidth = Math.max(1, Math.round(sourceCanvas.width * scale));
            const targetHeight = Math.max(1, Math.round(sourceCanvas.height * scale));

            const thumbCanvas = document.createElement('canvas');
            thumbCanvas.width = targetWidth;
            thumbCanvas.height = targetHeight;

            const thumbCtx = thumbCanvas.getContext('2d');
            thumbCtx.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);

            return thumbCanvas.toDataURL(mimeType, quality);
        } catch (error) {
            console.warn('Set-Vorschau konnte nicht als Thumbnail erzeugt werden:', error);
            return '';
        }
    }

    return { drawGlobal, getPreviewDataUrl, getPreviewThumbnailDataUrl };
})();

  /* =========================================
     Module: Navigation & Summary
     ========================================= */
  const NavigationModule = (function(){
    let lastScrollY = getScrollTop();
    let isScrollingUp = true;
    let isAtTop = lastScrollY <= 0;
    let scrollUpDistance = 0;
    let isTicking = false;
    let pendingScrollY = lastScrollY;
    let topHoldCount = 0;
    const SCROLL_UP_THRESHOLD = 60;

    function updateScrollState({ forceUpdate = false, currentScrollY = null } = {}) {
      if (!window.matchMedia || !window.matchMedia(MOBILE_FULLSCREEN_QUERY).matches) {
        if (forceUpdate) {
          isScrollingUp = true;
          isAtTop = true;
          scrollUpDistance = 0;
        }
        return;
      }
      const resolvedScrollY = Number.isFinite(currentScrollY)
        ? currentScrollY
        : getScrollTop();
      const currentIsAtTop = resolvedScrollY <= 0;
      const delta = resolvedScrollY - lastScrollY;

      if (currentIsAtTop) {
        if (delta < 0) {
          topHoldCount = 1;
        } else if (delta === 0 && topHoldCount > 0) {
          topHoldCount = Math.min(topHoldCount + 1, 2);
        }
        scrollUpDistance = SCROLL_UP_THRESHOLD;
      } else if (delta > 0) {
        scrollUpDistance = 0;
        topHoldCount = 0;
      } else if (delta < 0) {
        scrollUpDistance = Math.min(scrollUpDistance + Math.abs(delta), SCROLL_UP_THRESHOLD);
        topHoldCount = 0;
      }

      const hasConfirmedScrollUp = scrollUpDistance >= SCROLL_UP_THRESHOLD;
      const canRevealAtTop = currentIsAtTop && topHoldCount >= 2;
      const currentIsScrollingUp = canRevealAtTop || (!currentIsAtTop && hasConfirmedScrollUp);

      const directionChanged = currentIsScrollingUp !== isScrollingUp;
      const positionChanged = resolvedScrollY !== lastScrollY;
      const topChanged = currentIsAtTop !== isAtTop;

      if (forceUpdate || directionChanged || positionChanged || topChanged) {
        lastScrollY = resolvedScrollY;
        isScrollingUp = currentIsScrollingUp;
        isAtTop = currentIsAtTop;
        updateHeaderVisibility();
      }
    }

    function updateHeaderVisibility() {
      if (!DOM.wrapper || !DOM.header) return;
      const isMobile = window.matchMedia && window.matchMedia(MOBILE_FULLSCREEN_QUERY).matches;
      const shouldHide = state.currentStep >= 1 && (!isMobile || !(isAtTop && isScrollingUp));
      DOM.wrapper.classList.toggle('is-header-hidden', shouldHide);
      DOM.header.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');

      if (!document.body || !window.matchMedia) return;
      document.body.classList.toggle('set-konfigurator-site-header-transition', isMobile);
      document.body.classList.toggle('set-konfigurator-site-header-hidden', isMobile && shouldHide);
    }

    function updateProgressPositionClass() {
      if (!DOM.wrapper) return;
      const shouldRaise = state.currentStep >= 2;
      DOM.wrapper.classList.toggle('is-slot-2-plus', shouldRaise);
    }

    function goTo(n, { skipScroll = false } = {}) {
      if (n < 0 || n >= state.steps.length) return;

      // Alle Schritte ausblenden, bevor der neue Schritt aktiviert wird
      state.steps.forEach(step => step.classList.remove('active'));

      state.currentStep = n;
      state.steps[state.currentStep].classList.add('active');

      if (n >= 1 && n <= state.totalSlots) {
        applyDefaultSelections(state.steps[n]);
      }
      updateNavButtons();
      ProgressModule.updateActive();
      updateHeaderVisibility();
      updateProgressPositionClass();
      if (n === state.steps.length - 1) fillSummary();
      PreviewModule.drawGlobal(); // Vorschau bei jedem Schrittwechsel aktualisieren
      prefetchVariantsForStep(state.currentStep);
      prefetchVariantsForStep(state.currentStep + 1);

      // Zum Seitenanfang springen
      if (!skipScroll) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    function updateNavButtons() {
      DOM.prevBtn.disabled = (state.currentStep === 0);
      // "Zurück"-Button bei Case-Auswahl (Step 0) ausblenden
      DOM.prevBtn.style.display = (state.currentStep === 0 ? 'none' : 'inline-flex');
      if (DOM.restartBtn) {
        DOM.restartBtn.style.display = (state.currentStep === state.steps.length - 1 ? 'inline-flex' : 'none');
      }
      DOM.nextBtn.disabled = false;
      DOM.nextBtn.style.display = (state.currentStep < state.steps.length - 1 ? 'inline-flex' : 'none');
      DOM.cartBtn.style.display = (state.currentStep === state.steps.length - 1 ? 'inline-flex' : 'none');

      const shouldDisableNext = (() => {
        if (state.currentStep === 0) {
          return !state.selectedCaseId || state.selectedCaseId === 'null' || state.selectedCaseId === '';
        }
        if (state.currentStep <= state.totalSlots) {
          return !isStepValid(state.currentStep);
        }
        return true;
      })();

      DOM.nextBtn.classList.toggle('is-disabled', shouldDisableNext);
      DOM.nextBtn.setAttribute('aria-disabled', shouldDisableNext);

      updateCartPrice();

      console.log('Weiter-Button Zustand:', {
        step: state.currentStep,
        disabled: shouldDisableNext,
        selectedId: state.steps[state.currentStep]?.dataset.selectedId,
        engraveSelected: state.steps[state.currentStep]?.dataset.engrave
      }); // Debugging
    }

    function buildSlotDescription(step) {
      const type = step.dataset.type || strings.summary.empty;
      const blade = step.dataset.blade || strings.summary.empty;
      const handle = step.dataset.handle || strings.summary.empty;

      const engravingEnabled = isEngravingEnabled(step.dataset.engrave);
      const engravingInput = step.querySelector('.engraving-input');
      const engravingText = engravingInput?.value.trim() || '';
      const engravingChoice = engravingEnabled
        ? (engravingText || strings.summary.noText)
        : engraveNo;

      const parts = [type, blade, handle].filter(Boolean);
      parts.push(`${strings.summary.engraving}: ${engravingChoice}`);

      return parts.join(' · ');
    }

    function fillSummary() {
      let html = `<h2>${strings.progress.summary}</h2>`;
      const caseLabel = state.selectedCaseName || state.selectedCaseSku || state.selectedCaseId;
      const caseSummary = caseLabel
        ? `${caseLabel}${state.selectedCaseSku && state.selectedCaseName ? ` (${state.selectedCaseSku})` : ''}`
        : strings.summary.empty;
      html += `<p>${strings.summary.case}: ${caseSummary}</p>`;

      const slotEntries = [];

      for (let i = 1; i <= state.totalSlots; i++) {
        const step = state.steps[i];
        const variant = window.konfiguratorProdukte.find(p => String(p.id) === step.dataset.selectedId);
        const engravingEnabled = isEngravingEnabled(step.dataset.engrave);
        const skuRaw = variant?.sku || step.dataset.sku || '';
        const sku = resolveSkuWithEngraving(skuRaw, engravingEnabled) || strings.summary.empty;
        const description = buildSlotDescription(step);

        slotEntries.push(`
          <li class="summary-slot-item">
            <div class="summary-slot-title">${strings.summary.slot} ${i}</div>
            <ul class="summary-slot-details">
              <li><span class="summary-slot-label">${strings.summary.sku}:</span><span class="summary-slot-value">${sku}</span></li>
              <li><span class="summary-slot-label">${strings.summary.description}:</span><span class="summary-slot-value">${description}</span></li>
            </ul>
          </li>
        `);
      }

      html += `<ul class="summary-slot-list">${slotEntries.join('')}</ul>`;
      DOM.summaryDiv.innerHTML = html;
    }

    if (window.matchMedia) {
      const headerQuery = window.matchMedia(MOBILE_FULLSCREEN_QUERY);
      if (headerQuery.addEventListener) {
        headerQuery.addEventListener('change', () => {
          updateScrollState({ forceUpdate: true, currentScrollY: getScrollTop() });
          updateHeaderVisibility();
        });
      } else if (headerQuery.addListener) {
        headerQuery.addListener(() => {
          updateScrollState({ forceUpdate: true, currentScrollY: getScrollTop() });
          updateHeaderVisibility();
        });
      }
    }

    if (window.addEventListener) {
      updateScrollState({ forceUpdate: true, currentScrollY: lastScrollY });
      const handleScroll = () => {
        pendingScrollY = getScrollTop();
        if (isTicking || !window.requestAnimationFrame) {
          updateScrollState({ currentScrollY: pendingScrollY });
          return;
        }
        isTicking = true;
        window.requestAnimationFrame(() => {
          isTicking = false;
          updateScrollState({ currentScrollY: pendingScrollY });
        });
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      if (DOM.wrapper) {
        DOM.wrapper.addEventListener('scroll', handleScroll, { passive: true });
      }
      document.addEventListener('fullscreenchange', () => {
        updateScrollState({ forceUpdate: true, currentScrollY: getScrollTop() });
        updateHeaderVisibility();
      });
      document.addEventListener('webkitfullscreenchange', () => {
        updateScrollState({ forceUpdate: true, currentScrollY: getScrollTop() });
        updateHeaderVisibility();
      });
      document.addEventListener('mozfullscreenchange', () => {
        updateScrollState({ forceUpdate: true, currentScrollY: getScrollTop() });
        updateHeaderVisibility();
      });
      document.addEventListener('MSFullscreenChange', () => {
        updateScrollState({ forceUpdate: true, currentScrollY: getScrollTop() });
        updateHeaderVisibility();
      });
    }

    return { goTo, updateNavButtons, fillSummary, updateScrollState };
  })();

  function resetConfigurator() {
    const wizardContainer = DOM.wizardContainer;
    if (!wizardContainer) return;

    const step0 = wizardContainer.querySelector('[data-step="0"]');
    const stepEnd = wizardContainer.querySelector('[data-step="end"]');
    if (!step0 || !stepEnd) return;

    wizardContainer.querySelectorAll('.wizard-step[data-step]').forEach((step) => {
      if (step.dataset.step !== '0' && step.dataset.step !== 'end') {
        step.remove();
      }
    });

    step0.querySelectorAll('.case-option').forEach((btn) => btn.classList.remove('selected'));

    state.currentStep = 0;
    state.selectedCaseId = null;
    state.selectedCaseName = '';
    state.selectedCaseSku = '';
    state.casePriceCents = null;
    state.casePricePromise = Promise.resolve(null);
    state.totalSlots = 0;
    state.previewDataUrl = '';
    state.steps = [step0, stepEnd];

    if (DOM.summaryDiv) DOM.summaryDiv.innerHTML = '';
    if (DOM.previewCanvas) {
      ctx.clearRect(0, 0, DOM.previewCanvas.width, DOM.previewCanvas.height);
    }
    bgImg.src = '';

    ProgressModule.build();
    NavigationModule.goTo(0);
  }

  async function calculateTargetPriceCents() {
    const breakdown = [];

    await state.casePricePromise;

    const { variantId: setPriceVariantId, setPriceMapping } = resolveSetPriceVariantId();
    const hasEngraving = hasAnyEngravingSelected();

    const descriptiveLabel = (() => {
      if (!setPriceMapping) return 'Setpreis: unbekannte Set-Größe';
      const engravingLabel = hasEngraving ? 'mit Gravur' : 'ohne Gravur';
      return `${state.totalSlots}er Set (${engravingLabel})`;
    })();

    if (!setPriceMapping || !setPriceVariantId) {
      const errorText = strings.messages.setPriceVariantMissing || defaultStrings.messages.setPriceVariantMissing;
      breakdown.push(errorText);
      return { total: null, breakdown, setPriceError: errorText };
    }

    const setPriceValidation = await fetchVariantPriceWithValidation(setPriceVariantId);
    if (setPriceValidation.error) {
      breakdown.push(setPriceValidation.error);
      return { total: null, breakdown, setPriceError: setPriceValidation.error };
    }

    if (Number.isFinite(setPriceValidation.priceCents)) {
      breakdown.push(`${descriptiveLabel}: ${formatMoneyFromCents(setPriceValidation.priceCents)}`);
      return { total: setPriceValidation.priceCents, breakdown };
    }

    breakdown.push(`${descriptiveLabel}: Preis unbekannt`);

    let total = 0;

    const casePrice = Number.isFinite(state.casePriceCents)
      ? state.casePriceCents
      : await findPriceCentsById(state.selectedCaseId, { fetchIfMissing: true });

    if (Number.isFinite(casePrice)) {
      total += casePrice;
      breakdown.push(`Holzkästchen: ${formatMoneyFromCents(casePrice)}`);
    } else {
      breakdown.push('Holzkästchen: Preis unbekannt');
    }

    for (let i = 1; i <= state.totalSlots; i++) {
      const step = state.steps[i];
      if (!step) continue;

      const variantPrice = await findPriceCentsById(step.dataset.selectedId, { fetchIfMissing: true });
      if (Number.isFinite(variantPrice)) {
        total += variantPrice;
        breakdown.push(`Slot ${i}: ${formatMoneyFromCents(variantPrice)}`);
      } else {
        breakdown.push(`Slot ${i}: Preis unbekannt`);
      }

      if (isEngravingEnabled(step.dataset.engrave)) {
        const engravingPrice = await findPriceCentsById(ENGRAVING_VARIANT_ID, { fetchIfMissing: true });
        if (Number.isFinite(engravingPrice)) {
          total += engravingPrice;
          breakdown.push(`Gravur Slot ${i}: ${formatMoneyFromCents(engravingPrice)}`);
        } else {
          breakdown.push(`Gravur Slot ${i}: Preis unbekannt`);
        }
      }
    }

    return { total, breakdown };
  }

  async function updateCartPrice() {
    if (!DOM.cartPrice) return;
    const isSummaryStep = state.currentStep === state.steps.length - 1;
    if (!isSummaryStep) {
      DOM.cartPrice.textContent = '';
      DOM.cartPrice.style.display = 'none';
      return;
    }

    DOM.cartPrice.style.display = 'inline-flex';
    DOM.cartPrice.textContent = '…';
    const requestId = ++cartPriceRequestId;

    const targetPrice = await calculateTargetPriceCents();
    if (requestId !== cartPriceRequestId) return;

    if (Number.isFinite(targetPrice.total)) {
      DOM.cartPrice.textContent = formatMoneyFromCents(targetPrice.total);
      return;
    }

    DOM.cartPrice.textContent = 'Preis unbekannt';
  }

  /* =========================================
     Module: Warenkorb
     ========================================= */
  async function addToCart() {
    console.log('addToCart gestartet'); // Debugging
    try {
      await state.casePricePromise;

        const setId = window.crypto?.randomUUID
          ? window.crypto.randomUUID()
          : `set-${Date.now()}-${Math.random().toString(16).slice(2)}`;

        const encodedState = buildEncodedState();

        const { variantId: setPriceVariantId } = resolveSetPriceVariantId();
        const normalizedSetPriceVariantId = normalizeVariantId(setPriceVariantId);

        if (!normalizedSetPriceVariantId) {
          const errorText = strings.messages.setPriceVariantMissing || defaultStrings.messages.setPriceVariantMissing;
          alert(errorText);
          return;
        }

        const caseVariantId = normalizeVariantId(state.selectedCaseId);
        const priceCarrierId = normalizeVariantId(PRICE_CARRIER_VARIANT_ID);

        const targetPrice = await calculateTargetPriceCents();
        if (targetPrice.setPriceError) {
          alert(targetPrice.setPriceError);
          return;
        }
        const targetPriceTotal = Number.isFinite(targetPrice.total) ? targetPrice.total : null;
        const targetPriceProperties = {};

        const casePriceCents = Number.isFinite(state.casePriceCents)
          ? state.casePriceCents
          : await findPriceCentsById(caseVariantId, { fetchIfMissing: true });

      // Holzkästchen hinzufügen
        if (!caseVariantId) {
          console.error('Kein Holzkästchen ausgewählt');
          alert(strings.messages.noCaseSelected);
          return;
      }

        const baseProperties = {
          [SET_ID_PROPERTY_KEY]: setId,
          [SET_COMPONENT_PROPERTY_KEY]: 'Holzkästchen',
          [SET_STATE_PROPERTY_KEY]: encodedState,
          ...targetPriceProperties
        };

      if (state.selectedCaseSku) {
        baseProperties['SKU'] = state.selectedCaseSku;
      }

      // "Set Inhalt" ist die einzige Quelle der Wahrheit für den Set-Inhalt.
      // Vorher lagen dieselben Daten zusätzlich in _SKU Slot N / _Set Slot N /
      // _Gravurtext Slot N — der _-Prefix versteckt die nur im Theme, im Admin
      // stehen sie trotzdem alle in der Bestellung.
      const setSummaryLines = [];

      for (let i = 1; i <= state.totalSlots; i++) {
        const step = state.steps[i];
        if (!step || !step.dataset.selectedId) {
          console.error(`Keine Variante für Slot ${i} ausgewählt`);
          alert(formatString(strings.messages.noVariantSelected, { slot: i }));
          return;
        }

        const engravingInput = step.querySelector('.engraving-input');
        const engravingText = engravingInput?.value.trim() || '';
        const engravingEnabled = isEngravingEnabled(step.dataset.engrave);
        const engravingChoiceLabel = engravingEnabled ? engraveYes : engraveNo;
        const engravingValue = engravingEnabled
          ? (engravingText || strings.summary.noText)
          : engravingChoiceLabel;

        const slotSku = resolveSkuWithEngraving(step.dataset.sku || '', engravingEnabled);

        setSummaryLines.push(`Slot ${i}: SKU ${slotSku || '-'} · Gravur: ${engravingValue}`);
      }

      baseProperties[SET_CONTENT_PROPERTY_KEY] = setSummaryLines.join('\n');

        const previewCandidates = [
          { maxWidth: 180, quality: 0.55 },
          { maxWidth: 140, quality: 0.5 },
          { maxWidth: 110, quality: 0.45 },
          { maxWidth: 80, quality: 0.4 }
        ];
        let previewDataForSetItems = '';

        for (const candidate of previewCandidates) {
          const previewDataUrl = PreviewModule.getPreviewThumbnailDataUrl(candidate);
          if (previewDataUrl && previewDataUrl.length <= MAX_PREVIEW_DATA_URL_LENGTH) {
            previewDataForSetItems = previewDataUrl;
            break;
          }
        }

        if (!previewDataForSetItems) {
          const previewImageData = PreviewModule.getPreviewDataUrl();
          if (previewImageData && previewImageData.length <= MAX_PREVIEW_DATA_URL_LENGTH) {
            previewDataForSetItems = previewImageData;
          } else if (previewImageData) {
            console.warn('Set-Vorschau ist zu groß für den lokalen Cache und wurde ausgelassen.');
          }
        }

        // Das Vorschaubild geht bewusst NICHT mehr als Line-Item-Property mit
        // (base64-PNG, ~5 KB pro Bestellung). Der Warenkorb holt es über die SetID
        // aus dem localStorage — siehe snippets/set-preview-hydrate.liquid.
        storeSetPreviewLocally(setId, previewDataForSetItems);

        const setPriceProperties = {
          ...baseProperties,
          [SET_COMPONENT_PROPERTY_KEY]: 'Setpreis',
          [SET_PRICE_VARIANT_PROPERTY_KEY]: normalizedSetPriceVariantId
        };

        const setPriceCoversCompleteSet = Number.isFinite(targetPriceTotal);

        const items = [{
          id: normalizedSetPriceVariantId,
          quantity: 1,
          properties: setPriceProperties
        }];

        if (!setPriceCoversCompleteSet && caseVariantId) {
          items.push({
            id: caseVariantId,
            quantity: 1,
            properties: baseProperties
          });
        }

        const cartSumWithoutCarrier = await calculateCartItemsTotal(items);
        const priceCarrierDifferenceCents = Number.isFinite(targetPriceTotal)
          ? targetPriceTotal - cartSumWithoutCarrier
          : null;

        const priceCarrierApplied = Boolean(
          priceCarrierId
          && Number.isFinite(priceCarrierDifferenceCents)
          && priceCarrierDifferenceCents !== 0
        );

        if (priceCarrierApplied) {
          const priceCarrierProperties = sanitizeProperties({
            [SET_ID_PROPERTY_KEY]: setId,
            [SET_COMPONENT_PROPERTY_KEY]: 'Preis-Carrier',
            '_preis_authority': 'Preis-Carrier',
            '_preis_authority_betrag_cent': stringOrEmpty(priceCarrierDifferenceCents),
            [SET_STATE_PROPERTY_KEY]: encodedState,
            ...targetPriceProperties
          });

          items.push({
            id: priceCarrierId,
            quantity: 1,
            properties: priceCarrierProperties
          });
        }

        async function calculateCartItemsTotal(itemsList = []) {
          let totalCents = 0;

          for (const item of itemsList) {
            const normalizedId = normalizeVariantId(item.id);
            const variantPrice = await findPriceCentsById(normalizedId, { fetchIfMissing: true });
            if (Number.isFinite(variantPrice)) {
              totalCents += variantPrice * (item.quantity || 1);
            }
          }

          return totalCents;
        }

        const cartSum = await calculateCartItemsTotal(items);
        const priceAdjustmentPlanned = priceCarrierApplied || Number.isFinite(targetPriceTotal);
        const adjustedCartSum = priceAdjustmentPlanned && Number.isFinite(targetPriceTotal)
          ? targetPriceTotal
          : cartSum;

        if (Number.isFinite(adjustedCartSum)) {
          const verificationText = adjustedCartSum === targetPriceTotal
            ? 'Zielpreis und Warenkorbsumme identisch'
            : `Abweichung erkannt (Ziel: ${formatMoneyFromCents(targetPriceTotal)}, Warenkorb: ${formatMoneyFromCents(adjustedCartSum)})`;

          if (priceCarrierApplied) {
            const carrierItem = items.find((item) => item.properties?.[SET_COMPONENT_PROPERTY_KEY] === 'Preis-Carrier' || item.properties?.['Set-Komponente'] === 'Preis-Carrier');
            if (carrierItem?.properties) {
              carrierItem.properties['_preis_authority_hinweis'] = verificationText;
            }
          }
        }

        items.forEach((item) => {
          if (item.properties) {
            item.properties = sanitizeProperties(item.properties);
          }
        });

      console.log('Warenkorb-Items:', items); // Debugging

      // Shopify AJAX API-Aufruf
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        let errorText = '';

        try {
          errorText = await response.text();
        } catch (readError) {
          console.warn('Antwort konnte nicht gelesen werden:', readError); // Debugging
        }

        console.error('Fehlerhafte Antwort /cart/add.js:', response.status, errorText); // Debugging
        throw new Error(strings.messages.addToCartGenericError);
      }

      const data = await response.json();
      console.log('Erfolgreich zum Warenkorb hinzugefügt:', data); // Debugging
      const modalOpened = openCartModal();
      if (!modalOpened) {
        alert(strings.messages.addToCartSuccess);
        window.location.href = '/cart'; // Weiterleitung zum Warenkorb
      }
    } catch (e) {
      console.error('Fehler in addToCart:', e); // Debugging
      alert(strings.messages.addToCartError);
    }
  }

  /* =========================================
    Module: Initialization
     ========================================= */
  document.addEventListener('DOMContentLoaded', () => {
    console.log('Konfigurator Initialisierung gestartet'); // Debugging
    try {
      initCaseSelection();
      setupStickyOffsets();
      setupPreviewHitAreaInteractions();

      DOM.prevBtn.addEventListener('click', () => {
        console.log('Zurück-Button geklickt'); // Debugging
        NavigationModule.goTo(state.currentStep - 1);
      });
      DOM.nextBtn.addEventListener('click', () => {
        console.log('Weiter-Button geklickt'); // Debugging
        const nextDisabled = DOM.nextBtn.classList.contains('is-disabled');
        if (nextDisabled || !isStepValid(state.currentStep)) {
          highlightMissingForStep(state.currentStep);
          return;
        }
        NavigationModule.goTo(state.currentStep + 1);
      });
      DOM.cartBtn.addEventListener('click', () => {
        console.log('Warenkorb-Button geklickt'); // Debugging
        addToCart();
      });
      if (DOM.cartModal) {
        DOM.cartModal.addEventListener('click', (event) => {
          if (event.target.matches('[data-cart-modal-close]')) {
            event.preventDefault();
            closeCartModal();
          }
        });
      }
      if (DOM.cartModalViewCart) {
        DOM.cartModalViewCart.addEventListener('click', () => {
          window.location.href = '/cart';
        });
      }
      if (DOM.cartModalNewSet) {
        DOM.cartModalNewSet.addEventListener('click', () => {
          const baseUrl = `${window.location.origin}${window.location.pathname}`;
          window.location.assign(baseUrl);
        });
      }
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && DOM.cartModal?.classList.contains('is-open')) {
          closeCartModal();
        }
        if (event.key === 'Escape' && DOM.restartModal?.classList.contains('is-open')) {
          closeRestartModal();
        }
      });
      if (DOM.restartModal) {
        DOM.restartModal.addEventListener('click', (event) => {
          if (event.target.matches('[data-restart-modal-close]')) {
            closeRestartModal();
          }
        });
      }
      if (DOM.restartModalConfirm) {
        DOM.restartModalConfirm.addEventListener('click', () => {
          closeRestartModal();
          const baseUrl = `${window.location.origin}${window.location.pathname}`;
          window.location.assign(baseUrl);
        });
      }
      if (DOM.restartModalCancel) {
        DOM.restartModalCancel.addEventListener('click', closeRestartModal);
      }
      if (DOM.restartBtn) {
        DOM.restartBtn.addEventListener('click', () => {
          const opened = openRestartModal();
          if (!opened) {
            const confirmText = strings.messages.confirmRestart || defaultStrings.messages.confirmRestart;
            if (!window.confirm(confirmText)) return;
            const baseUrl = `${window.location.origin}${window.location.pathname}`;
            window.location.assign(baseUrl);
          }
        });
      }

      NavigationModule.goTo(0);
      initializeFromQueryState();
      console.log('Initialisierung abgeschlossen'); // Debugging
    } catch (e) {
      console.error('Fehler bei der Konfigurator-Initialisierung:', e); // Debugging
    }
  });
})();